import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { asList } from '../utils/api';
import API from '../utils/apiconfig';
import { clearMemberStorage, persistMemberTokens } from '../utils/authSession';
import TablePagination from '../components/TablePagination';
import ScrollableTable, { ScrollableListBody } from '../components/ScrollableTable';
import MediaPreviewModal, { handleMediaView, resolveMediaSrc } from '../components/MediaPreviewModal';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';
import { getApiErrorMessage } from '../utils/formErrors';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';
import { usePasswordVisibility, PasswordVisibilityToggle, PASSWORD_INPUT_CLASS } from '../components/PasswordVisibilityToggle';
import ReferralHistoryTab from './admin/ReferralHistoryTab';
import MlmCommissionLogTab from './admin/MlmCommissionLogTab';
import WithdrawalsTab from './admin/WithdrawalsTab';
import TruncateText, { TruncateMemberCell } from '../components/TruncateText';
import AppButton from '../components/ui/AppButton';
import { formatAppDate, formatAppDateTime } from '../utils/dateFormat';

// ════════════════════════════════════════════════════════════════
//  SHARED UI PRIMITIVES
// ════════════════════════════════════════════════════════════════

const Label = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">{children}</p>
);

const FieldInput = ({ label, type = 'text', className = '', ...props }) => {
  const isPassword = type === 'password';
  const { visible, toggle, inputType } = usePasswordVisibility();

  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className={isPassword ? 'relative' : undefined}>
        <input
          className={`w-full py-2.5 border border-slate-200 rounded-xl bg-white text-slate-800 text-sm outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition placeholder-slate-300 ${
            isPassword ? `pl-3.5 pr-10 ${PASSWORD_INPUT_CLASS}` : 'px-3.5'
          } ${className}`}
          type={isPassword ? inputType : type}
          {...props}
        />
        {isPassword ? <PasswordVisibilityToggle visible={visible} onToggle={toggle} /> : null}
      </div>
    </div>
  );
};

const FieldTextarea = ({ label, ...props }) => (
  <div>
    {label && <Label>{label}</Label>}
    <textarea
      className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl bg-white text-slate-800 text-sm outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition placeholder-slate-300 resize-none font-[inherit]"
      rows={4}
      {...props}
    />
  </div>
);

const FieldSelect = ({ label, children, ...props }) => (
  <div>
    {label && <Label>{label}</Label>}
    <select
      className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl bg-white text-slate-800 text-sm outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition"
      {...props}
    >
      {children}
    </select>
  </div>
);

/** Inline key–value row for admin user preview (label left, value right). */
const DetailKvRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest shrink-0 max-w-[46%] leading-snug">
      {label}
    </span>
    <span className="text-sm text-slate-900 font-semibold text-right min-w-0 flex-1 break-words leading-snug">
      {value}
    </span>
  </div>
);

const PrimaryBtn = ({ children, className = '', icon, ...props }) => (
  <AppButton variant="primary" size="lg" icon={icon} className={className} {...props}>{children}</AppButton>
);
const GhostBtn = ({ children, className = '', icon, ...props }) => (
  <AppButton variant="secondary" size="lg" icon={icon} className={className} {...props}>{children}</AppButton>
);
const DangerBtn = ({ children, className = '', icon = 'x', ...props }) => (
  <AppButton variant="danger" size="lg" icon={icon} className={className} {...props}>{children}</AppButton>
);
const RefreshBtn = ({ children = 'Refresh', className = '', ...props }) => (
  <AppButton variant="refresh" size="sm" icon="refresh" className={className} {...props}>{children}</AppButton>
);

const ErrBox = ({ msg }) => msg ? (
  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">⚠️ {msg}</div>
) : null;

function Toast({ msg, variant = 'default' }) {
  if (!msg) return null;
  const variantClass =
    variant === 'success'
      ? 'bg-emerald-600 text-white'
      : variant === 'error'
        ? 'bg-red-600 text-white'
        : 'bg-slate-900 text-white';
  return (
    <div
      className={`fixed top-5 right-5 z-[100] text-sm font-semibold px-5 py-3 rounded-2xl shadow-2xl ${variantClass}`}
    >
      {msg}
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, closeLeft = false, maxWidthClass = 'max-w-lg', largeClose = false, bodyClassName = 'p-6' }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  const closeBtn = (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className={`rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0 leading-none ${
        largeClose ? 'w-12 h-12 text-3xl font-light' : 'w-9 h-9 text-xl'
      }`}
    >
      ×
    </button>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${maxWidthClass} max-h-[92vh] overflow-y-auto z-10`}>
        <div
          className={`flex items-start px-6 py-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl ${
            closeLeft ? 'gap-3' : 'justify-between'
          }`}
        >
          {closeLeft ? (
            <>
              {closeBtn}
              <div className="min-w-0 flex-1 pr-2">
                <h2 className="text-base font-black text-slate-900 tracking-tight">{title}</h2>
                {subtitle && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
            </>
          ) : (
            <>
              <div className="min-w-0 pr-2">
                <h2 className="text-base font-black text-slate-900 tracking-tight">{title}</h2>
                {subtitle && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
              {closeBtn}
            </>
          )}
        </div>
        <div className={bodyClassName}>{children}</div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, colorClass = 'text-slate-900' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <span className="text-2xl">{icon}</span>
      <p className={`text-2xl font-black mt-2 ${colorClass}`}>{value}</p>
      <p className="text-xs font-semibold text-slate-400 mt-1">{label}</p>
    </div>
  );
}

const RANK_PILL = {
  bronze: 'bg-orange-50 text-orange-600 border-orange-200',
  silver: 'bg-slate-100 text-slate-500 border-slate-300',
  gold: 'bg-amber-50 text-amber-600 border-amber-200',
  platinum: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  diamond: 'bg-violet-50 text-violet-600 border-violet-200',
};
const RANK_ICON = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '✨' };

function RankBadge({ member }) {
  const rankBadge = getRankDisplay(member);
  if (!rankBadge.show) {
    return <span className="text-[10px] font-medium text-slate-400">—</span>;
  }
  const { colorClass, icon } = rankTheme(rankBadge.themeKey, RANK_PILL, RANK_ICON);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold px-2 py-0.5 rounded-full border ${colorClass}`}
    >
      <span className="flex-shrink-0 leading-none">{icon}</span>
      <span>{rankBadge.label}</span>
    </span>
  );
}

const MEMBERSHIP_STATUS_LABELS = {
  active: 'Active Member',
  pending: 'Awaiting Approval',
  rejected: 'Approval Rejected',
  expired: 'Membership Expired',
  cancelled: 'Membership Cancelled',
  none: 'No Membership Plan',
  deactivated: 'Account Deactivated',
};

function MembershipBadge({ status }) {
  const s = {
    active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    expired: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
    pending: 'bg-amber-50 text-amber-800 border-amber-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    none: 'bg-slate-100 text-slate-700 border-slate-200',
    deactivated: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  const label = MEMBERSHIP_STATUS_LABELS[status] || MEMBERSHIP_STATUS_LABELS.none;
  return (
    <span className={`inline-flex items-center text-[10px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${s[status] || s.none}`}>
      {label}
    </span>
  );
}

function AccountStatusBadge({ member, showDescription = false }) {
  const key = resolveAccountDisplayStatus(member);
  const cfg = ACCOUNT_STATUS_BADGE[key];
  return (
    <div className={showDescription ? 'space-y-2' : ''}>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border whitespace-nowrap ${cfg.className}`}>
        <span aria-hidden="true">{cfg.icon}</span>
        {cfg.label}
      </span>
      {showDescription && cfg.description && (
        <p className="text-sm text-slate-600 leading-relaxed">{cfg.description}</p>
      )}
    </div>
  );
}

const MEMBER_ACCOUNT_STATUS_FILTERS = [
  { value: 'active', label: 'Active Members' },
  { value: 'archived', label: 'Archived Members' },
];

const ADMIN_PAYMENT_DETAIL_FIELDS = [
  ['id', 'Record ID'],
  ['username', 'Username'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['referral_code', 'Referral code'],
  ['current_plan', 'Plan'],
  ['current_payment', 'This payment'],
  ['total_paid', 'Total paid'],
  ['remaining', 'Remaining balance'],
  ['payment_proof', 'Payment proof (path)'],
  ['reward_points', 'Reward points'],
  ['total_earned_points', 'Lifetime earned'],
  ['rank', 'Rank slug'],
  ['level_label', 'Rank badge'],
  ['show_badge', 'Badge visible'],
  ['referral_count', 'Network (referrals)'],
  ['referred_by_username', 'Referred by'],
  ['membership_status', 'Membership'],
  ['days_remaining', 'Days remaining'],
  ['network_tier', 'Network tier'],
  ['bank_account_number', 'Bank account'],
  ['bank_ifsc', 'IFSC'],
  ['bank_name', 'Bank name'],
  ['upi_id', 'UPI ID'],
  ['account_holder_name_as_per_bank', 'Account holder (bank)'],
  ['is_staff', 'Staff'],
  ['is_superuser', 'Superuser'],
  ['date_joined', 'Joined'],
];

function formatMemberDetailValue(key, val) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (key === 'date_joined' && val) {
    return formatAppDateTime(val, String(val));
  }
  if (
    typeof val === 'number' &&
    Number.isFinite(val) &&
    (key.includes('points') || key === 'referral_count' || key === 'days_remaining' || key === 'id')
  ) {
    return val.toLocaleString('en-IN');
  }
  return String(val);
}

function formatPaymentDetailValue(key, val) {
  if (val === null || val === undefined || val === '') return '—';
  if (['total_paid', 'current_payment', 'remaining'].includes(key)) {
    const n = Number(val);
    if (!Number.isFinite(n)) return String(val);
    return `₹${n.toLocaleString('en-IN')}`;
  }
  return formatMemberDetailValue(key, val);
}

const ADMIN_PLAN_LABEL_EMI = 'Monthly EMI Plan';
const ADMIN_PLAN_LABEL_FULL = 'Full Settlement';

function resolveAdminPlanTypeMeta(source = {}) {
  const cp = source.current_plan && typeof source.current_plan === 'object' ? source.current_plan : source;
  const isFullSettlement =
    cp.is_full_settlement === true ||
    source.is_full_plan_settlement === true ||
    source.plan_is_full_settlement === true ||
    cp.type_of_plan === 'FULL_SETTLEMENT' ||
    source.type_of_plan === 'FULL_SETTLEMENT';
  const label = isFullSettlement
    ? ADMIN_PLAN_LABEL_FULL
    : (cp.plan_name ??
      cp.type_of_plan_label ??
      source.type_of_plan_label ??
      source.current_plan ??
      ADMIN_PLAN_LABEL_EMI);
  return { isFullSettlement, label };
}

function AdminPlanTypeBadge({ isFullSettlement, label, className = '' }) {
  const meta = isFullSettlement
    ? {
        label: label || ADMIN_PLAN_LABEL_FULL,
        className: 'bg-amber-100 text-amber-950 border-amber-300',
        dotClassName: 'bg-amber-500',
      }
    : {
        label: label || ADMIN_PLAN_LABEL_EMI,
        className: 'bg-violet-100 text-violet-950 border-violet-300',
        dotClassName: 'bg-violet-600',
      };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border leading-none ${meta.className} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dotClassName}`} aria-hidden />
      {meta.label}
    </span>
  );
}

const PAYMENT_VALIDATION_BADGE = {
  amount_correct: { label: 'Amount Correct', className: 'bg-emerald-100 text-emerald-950 border-emerald-300', dot: 'bg-emerald-500' },
  amount_mismatch: { label: 'Amount Mismatch', className: 'bg-red-100 text-red-950 border-red-300', dot: 'bg-red-500' },
  under_payment: { label: 'Under Payment', className: 'bg-orange-100 text-orange-950 border-orange-300', dot: 'bg-orange-500' },
  over_payment: { label: 'Over Payment', className: 'bg-purple-100 text-purple-950 border-purple-300', dot: 'bg-purple-500' },
  duplicate_payment: { label: 'Duplicate Payment', className: 'bg-indigo-100 text-indigo-950 border-indigo-300', dot: 'bg-indigo-500' },
  already_approved: { label: 'Already Approved', className: 'bg-slate-200 text-slate-800 border-slate-300', dot: 'bg-slate-500' },
  not_pending: { label: 'Not Pending', className: 'bg-slate-200 text-slate-800 border-slate-300', dot: 'bg-slate-500' },
  wrong_installment: { label: 'Wrong EMI Month', className: 'bg-red-100 text-red-950 border-red-300', dot: 'bg-red-500' },
  pending_validation: { label: 'Pending Validation', className: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400' },
  blocked: { label: 'Blocked', className: 'bg-red-100 text-red-950 border-red-300', dot: 'bg-red-500' },
  legacy_amount_mismatch: {
    label: 'Underpaid approval',
    className: 'bg-orange-100 text-orange-950 border-orange-300',
    dot: 'bg-orange-500',
  },
};

function PaymentHistoryAmountMismatchBadge({ row }) {
  if (!row?.amount_plan_mismatch) return null;
  const meta = PAYMENT_VALIDATION_BADGE.legacy_amount_mismatch;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border leading-none mt-1 ${meta.className}`}
      title={row.amount_mismatch_message || meta.label}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} aria-hidden />
      {meta.label}
    </span>
  );
}

function PaymentValidationBadge({ validation = {}, className = '' }) {
  const status = validation.validation_status || 'pending_validation';
  const meta = PAYMENT_VALIDATION_BADGE[status] || PAYMENT_VALIDATION_BADGE.pending_validation;
  const label = validation.validation_label || meta.label;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full border leading-none ${meta.className} ${className}`}
      title={validation.approval_blocked_reason || validation.messages?.join(' ') || label}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} aria-hidden />
      {label}
    </span>
  );
}

function PaymentValidationSummary({ row, fmtMoney }) {
  const v = row.payment_validation || {};
  const blockedNote = friendlyAdminValidationNote(v.approval_blocked_reason);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <PaymentValidationBadge validation={v} />
        {row.was_approval_reverted && String(row.proof_status || v.proof_status || '').toUpperCase() === 'PENDING' && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900">
            Sent back once
          </span>
        )}
      </div>
      <p><span className="text-slate-500">Should pay:</span> <strong>{fmtMoney(v.expected_amount ?? row.expected_amount)}</strong></p>
      <p><span className="text-slate-500">Member paid:</span> <strong>{fmtMoney(v.paid_amount ?? row.current_payment)}</strong></p>
      <p>
        <span className="text-slate-500">Difference:</span>{' '}
        <strong className={Number(v.difference_amount) === 0 ? 'text-emerald-700' : 'text-red-700'}>
          {fmtMoney(Math.abs(Number(v.difference_amount ?? 0)))}
          {Number(v.difference_amount) > 0 ? ' less than expected' : Number(v.difference_amount) < 0 ? ' more than expected' : ''}
        </strong>
      </p>
      {blockedNote && (
        <p className="text-amber-900 font-semibold leading-snug">{blockedNote}</p>
      )}
    </div>
  );
}

/** Turn backend validation text into short, plain language for non-technical admins. */
function friendlyAdminValidationNote(reason) {
  const r = String(reason || '').trim();
  if (!r) return '';
  if (/already approved/i.test(r)) {
    return '✓ This payment is already marked as Paid. If that was a mistake, use the yellow box below.';
  }
  if (/already rejected/i.test(r)) {
    return 'This payment was already marked as Rejected.';
  }
  if (/full settlement.*already approved/i.test(r)) {
    return 'Full payment for this plan is already marked as Paid.';
  }
  if (/installment.*already approved/i.test(r)) {
    return 'This month\'s EMI is already marked as Paid.';
  }
  return r;
}

function AdminUndoWhatHappens() {
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-2.5">
      <p className="text-xs font-bold text-slate-800">What will change:</p>
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-bold flex-wrap">
        <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-2.5 py-1.5 text-emerald-800">
          ✓ Paid
        </span>
        <span className="text-slate-400">→</span>
        <span className="inline-flex items-center gap-1 rounded-lg bg-amber-100 px-2.5 py-1.5 text-amber-900">
          ⏳ Waiting for check
        </span>
      </div>
      <ul className="list-none space-y-1.5 text-xs text-slate-600 leading-relaxed">
        <li>• Payment goes back to the <strong>Payments</strong> waiting list</li>
        <li>• You can open the payment photo and choose Paid or Reject again</li>
        <li>• Member is <strong>not counted as paid</strong> until you approve again</li>
      </ul>
    </div>
  );
}

function AdminUndoApprovalPanel({
  phase = 'intro',
  revertReason,
  onRevertReasonChange,
  onStartConfirm,
  onCancelConfirm,
  onSubmit,
  submitting = false,
}) {
  if (phase === 'intro') {
    return (
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none" aria-hidden>⚠️</span>
          <div>
            <p className="text-base font-black text-amber-950">Clicked Approve by mistake?</p>
            <p className="text-sm text-amber-900 mt-1 leading-relaxed">
              Use this only if the wrong payment was marked as <strong>Paid</strong>.
            </p>
          </div>
        </div>
        <AdminUndoWhatHappens />
        <button
          type="button"
          onClick={onStartConfirm}
          className="w-full sm:w-auto px-5 py-3 text-sm font-bold text-amber-950 border-2 border-amber-400 rounded-xl bg-white hover:bg-amber-100 min-h-[44px]"
        >
          ↩ Revert back to Waiting list
        </button>
        <p className="text-[11px] text-amber-800 leading-relaxed">
          Important: you can do this <strong>only one time</strong> for this payment.
          Points given to the member may need a manual check.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4 space-y-3">
      <div>
        <p className="text-base font-black text-red-950">Please confirm</p>
        <p className="text-sm text-red-900 mt-1 leading-relaxed">
          This payment will show as <strong>Waiting for check</strong> again — not Paid.
        </p>
      </div>
      <FieldTextarea
        label="What went wrong? (write at least 10 letters)"
        placeholder="Example: I marked the wrong member as Paid."
        value={revertReason}
        onChange={(e) => onRevertReasonChange(e.target.value)}
        rows={3}
      />
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={onCancelConfirm}
          disabled={submitting}
          className="flex-1 px-4 py-3 text-sm font-semibold border border-slate-200 rounded-xl bg-white min-h-[44px]"
        >
          No — keep as Paid
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || revertReason.trim().length < 10}
          className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 min-h-[44px]"
        >
          {submitting ? 'Please wait…' : 'Yes — revert back to Waiting'}
        </button>
      </div>
    </div>
  );
}

const PAYMENT_HISTORY_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'approved', label: 'Paid' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'pending', label: 'Waiting' },
  { id: 'amount_mismatch', label: 'Underpaid approvals', accent: true },
];

/** Detect legacy/wrong approvals where Paid amount ≠ plan expectation (client + API). */
function detectApprovedAmountPlanMismatch(item, planMeta, cp, paid, proofStatus) {
  if (item.amount_plan_mismatch === true) {
    return {
      amount_plan_mismatch: true,
      amount_mismatch_message: item.amount_mismatch_message ?? '',
      expected_amount: item.expected_amount ?? item.payment_validation?.expected_amount,
      paid_amount: item.paid_amount ?? String(paid),
      difference_amount: item.difference_amount ?? item.payment_validation?.difference_amount,
    };
  }
  if (String(proofStatus || '').toUpperCase() !== 'APPROVED') {
    return {
      amount_plan_mismatch: false,
      amount_mismatch_message: '',
      expected_amount: null,
      paid_amount: null,
      difference_amount: null,
    };
  }
  const planTotal = Number(cp.amt_of_plan ?? item.amt_of_plan ?? 0);
  const credited = Number(cp.total_paid ?? 0);
  if (planMeta.isFullSettlement && planTotal > 0 && credited > 0 && credited < planTotal) {
    const diff = planTotal - credited;
    const label = planMeta.label || ADMIN_PLAN_LABEL_FULL;
    return {
      amount_plan_mismatch: true,
      amount_mismatch_message: `${label} is marked Paid but only ₹${credited} of ₹${planTotal} is credited on the member plan. Use Revert approval to send back for correction.`,
      expected_amount: String(planTotal),
      paid_amount: String(paid),
      difference_amount: String(diff),
    };
  }
  const expected = planMeta.isFullSettlement
    ? Number(cp.amt_of_plan ?? item.amt_of_plan ?? 0)
    : Number(cp.monthly_emi_amount ?? 0);
  if (!Number.isFinite(expected) || expected <= 0 || paid === expected) {
    return {
      amount_plan_mismatch: false,
      amount_mismatch_message: '',
      expected_amount: String(expected),
      paid_amount: String(paid),
      difference_amount: String(expected - paid),
    };
  }
  const diff = expected - paid;
  const label = planMeta.label || (planMeta.isFullSettlement ? ADMIN_PLAN_LABEL_FULL : ADMIN_PLAN_LABEL_EMI);
  const message = paid < expected
    ? `Marked Paid with ₹${paid} but ${label} expected ₹${expected} (short by ₹${diff}). Use Revert approval to send back for correction.`
    : `Marked Paid with ₹${paid} but expected ₹${expected} (₹${Math.abs(diff)} over). Review and revert if this was a mistake.`;
  return {
    amount_plan_mismatch: true,
    amount_mismatch_message: message,
    expected_amount: String(expected),
    paid_amount: String(paid),
    difference_amount: String(diff),
  };
}

const PAYMENT_PROOF_STATUS_BADGE = {
  APPROVED: { label: 'Paid', className: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-900 border-red-200' },
  PENDING: { label: 'Waiting', className: 'bg-amber-100 text-amber-950 border-amber-200' },
  reverted: { label: 'Sent back once', className: 'bg-orange-100 text-orange-950 border-orange-200' },
};

function paymentProofStatusBadge(row) {
  const key = String(row.proof_status || '').toUpperCase();
  // "Sent back once" only while the proof is actually pending review again.
  if (row.was_approval_reverted && key === 'PENDING') {
    return PAYMENT_PROOF_STATUS_BADGE.reverted;
  }
  return PAYMENT_PROOF_STATUS_BADGE[key] || {
    label: row.proof_status_label || key || 'Unknown',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  };
}

/** Map v2 payment queue item to the flat row shape used by PaymentsTab UI */
function normalizePaymentQueueRow(item) {
  if (!item || typeof item !== 'object') return item;
  if (item.username && item.current_payment != null && !item.user_details && !item.payment_proof_id) {
    return item;
  }

  const ud = item.user_details || {};
  const u = item.user || {};
  const cp = item.current_plan || {};
  const planMeta = resolveAdminPlanTypeMeta({ ...item, current_plan: cp });
  const paid = Number(item.payment_amount ?? item.payment?.amount ?? 0);
  const proofStatus = item.proof_status ?? item.status ?? '';
  const mismatchInfo = detectApprovedAmountPlanMismatch(item, planMeta, cp, paid, proofStatus);
  let pv = item.payment_validation || {};
  if (!pv.validation_status) {
    const expected = planMeta.isFullSettlement
      ? Number(cp.amt_of_plan ?? item.amt_of_plan ?? 0)
      : Number(cp.monthly_emi_amount ?? 0);
    const diff = expected - paid;
    let status = 'pending_validation';
    let canApprove = false;
    if (expected > 0 && paid === expected) {
      status = 'amount_correct';
      canApprove = true;
    } else if (expected > 0 && paid < expected) {
      status = 'under_payment';
    } else if (expected > 0 && paid > expected) {
      status = 'over_payment';
    } else if (expected > 0) {
      status = 'amount_mismatch';
    }
    pv = {
      validation_status: status,
      validation_label: PAYMENT_VALIDATION_BADGE[status]?.label || status,
      expected_amount: String(expected),
      paid_amount: String(paid),
      difference_amount: String(diff),
      can_approve: canApprove,
      approval_blocked_reason: canApprove ? '' : `Expected ₹${expected}, customer paid ₹${paid}.`,
    };
  }
  if (mismatchInfo.amount_plan_mismatch) {
    pv = {
      ...pv,
      expected_amount: mismatchInfo.expected_amount,
      paid_amount: mismatchInfo.paid_amount,
      difference_amount: mismatchInfo.difference_amount,
      validation_status: Number(mismatchInfo.difference_amount) > 0 ? 'under_payment' : 'over_payment',
      validation_label: PAYMENT_VALIDATION_BADGE.legacy_amount_mismatch.label,
      approval_blocked_reason: mismatchInfo.amount_mismatch_message,
    };
  }

  return {
    id: item.payment_proof_id ?? item.id ?? u.id,
    payment_proof_id: item.payment_proof_id,
    username: ud.username ?? u.username ?? '',
    email: ud.email ?? u.email ?? '',
    phone: ud.phone ?? '',
    referral_code: ud.referral_code ?? '',
    reward_points: ud.reward_points ?? 0,
    total_earned_points: ud.total_earned_points ?? 0,
    rank: ud.rank ?? ud.level ?? '',
    level: ud.level ?? ud.rank ?? '',
    level_label: ud.level_label ?? '',
    show_badge: ud.show_badge ?? false,
    ui_key: ud.ui_key ?? '',
    referral_count: ud.referral_count ?? 0,
    referred_by_username: ud.referred_by_username ?? null,
    membership_status: ud.membership_status ?? '',
    days_remaining: ud.days_remaining ?? 0,
    bank_account_number: ud.bank_account_number ?? '',
    bank_ifsc: ud.bank_ifsc ?? '',
    bank_name: ud.bank_name ?? '',
    upi_id: ud.upi_id ?? '',
    account_holder_name_as_per_bank: ud.account_holder_name_as_per_bank ?? '',
    network_tier: ud.network_tier ?? '',
    is_staff: ud.is_staff ?? false,
    is_superuser: ud.is_superuser ?? false,
    date_joined: ud.date_joined ?? null,
    payment_proof: item.screenshot_url ?? item.payment?.screenshot_url ?? '',
    current_plan: planMeta.label,
    plan_is_full_settlement: planMeta.isFullSettlement,
    payment_validation: pv,
    can_approve: pv.can_approve === true,
    expected_amount: mismatchInfo.expected_amount ?? pv.expected_amount,
    paid_amount: mismatchInfo.paid_amount ?? pv.paid_amount,
    difference_amount: mismatchInfo.difference_amount ?? pv.difference_amount,
    was_approval_reverted: pv.was_approval_reverted === true || item.was_approval_reverted === true,
    uploaded_at: item.uploaded_at ?? pv.uploaded_at ?? null,
    proof_status: item.proof_status ?? item.status ?? '',
    proof_status_label: item.proof_status_label ?? '',
    installment_label: item.installment_label ?? '',
    amount_plan_mismatch: mismatchInfo.amount_plan_mismatch === true,
    amount_mismatch_message: mismatchInfo.amount_mismatch_message ?? item.amount_mismatch_message ?? '',
    transaction_id: item.transaction_id ?? item.payment?.transaction_id ?? '',
    reviewed_at: item.reviewed_at ?? null,
    reviewed_by_username: item.reviewed_by_username ?? '',
    admin_note: item.admin_note ?? '',
    can_revert_approval: item.can_revert_approval === true,
    approval_revert_reason: item.approval_revert_reason ?? '',
    attempt_no: item.attempt_no ?? 1,
    total_paid: Number(cp.total_paid ?? 0),
    current_payment: Number(item.payment_amount ?? item.payment?.amount ?? 0),
    remaining: Number(cp.remaining_amount ?? 0),
  };
}

/** Demo catalog until `GET /api/admin/products/` is available */
const DUMMY_ADMIN_PRODUCTS = [
  { id: 1, name: 'Electronics starter kit', price: 5000, image: '/images/i_electronics.png', sku: 'ELC-001' },
  { id: 2, name: 'i Mobile handset', price: 12999, image: '/images/i_mobile.png', sku: 'MOB-220' },
  { id: 3, name: 'i Marts gift card', price: 2500, image: '/images/i_marts.png', sku: 'MRT-050' },
  { id: 4, name: 'Textiles premium pack', price: 3499, image: '/images/i_textiles.png', sku: 'TXT-88' },
  { id: 5, name: 'Trading Academy course', price: 8999, image: '/images/i_trading_academy.png', sku: 'EDU-PRO' },
  { id: 6, name: 'Repair center toolkit', price: 599, image: '/images/i_mobile_repair_centers.png', sku: 'REP-KIT' },
  { id: 7, name: 'I Watch reward', price: 7999, image: '/images/rewards/i_watch.png', sku: 'RWD-WCH' },
  { id: 8, name: 'Tablet reward', price: 15999, image: '/images/rewards/tab.png', sku: 'RWD-TAB' },
];

// ── Helper: get admin token headers ────────────────────────────
const adminHeaders = () => {
  const token = localStorage.getItem('admin_access_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (localStorage.getItem('admin_impersonation_mode') === 'true') {
    headers['X-Impersonation-Mode'] = 'true';
  }
  return headers;
};

const isImpersonationMode = () => localStorage.getItem('admin_impersonation_mode') === 'true';

const isAdministrativeAccount = (u) => u?.role === 'admin' || u?.is_superuser || u?.is_staff;

const isMemberArchived = (member) =>
  member?.is_active === false || member?.status === 'archived';

const resolveAccountDisplayStatus = (member) => {
  if (isMemberArchived(member)) return 'archived';
  if (member?.is_blocked) return 'blocked';
  const membershipStatus = member?.membership_status;
  if (membershipStatus === 'pending') return 'pending';
  if (membershipStatus === 'rejected') return 'rejected';
  return 'active';
};

const ACCOUNT_STATUS_BADGE = {
  active: {
    label: 'Active Member',
    icon: '🟢',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    description: 'This member has an active account and can use membership benefits.',
  },
  pending: {
    label: 'Awaiting Approval',
    icon: '🟡',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
    description: 'This member is waiting for payment approval before full access is granted.',
  },
  rejected: {
    label: 'Approval Rejected',
    icon: '🔴',
    className: 'bg-red-50 text-red-700 border-red-200',
    description: 'A recent payment was not approved. The member may need to submit payment again.',
  },
  blocked: {
    label: 'Payment Overdue - Account Restricted',
    icon: '🚫',
    className: 'bg-orange-50 text-orange-800 border-orange-200',
    description: 'This member missed required payments and currently cannot access membership benefits.',
  },
  archived: {
    label: 'Archived Member',
    icon: '📦',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
    description: 'This member has been moved to archived records and is not part of active operations.',
  },
};

const ADMIN_ACTION_TOOLTIPS = {
  resetPassword: 'Use this option only when a member cannot access their account.',
  updateDetails: 'Update the member\'s contact and profile information.',
  archive: 'Archived members are removed from active operations but their records remain available.',
  restore: 'Bring this member back into the active member list.',
  reactivate: 'Open Account Recovery to collect overdue EMI payments before restoring access.',
  bonus: 'Add reward points to this member\'s account.',
};

const canResetMemberPassword = (adminUser, target) => {
  if (!adminUser?.is_superuser) return false;
  if (!target || isImpersonationMode()) return false;
  if (isAdministrativeAccount(target)) return false;
  if (Number(adminUser?.id) === Number(target?.id)) return false;
  return true;
};

const canArchiveMember = (adminUser, target) => {
  if (!target || isImpersonationMode()) return false;
  if (isAdministrativeAccount(target)) return false;
  if (Number(adminUser?.id) === Number(target?.id)) return false;
  return !isMemberArchived(target) && !target?.is_blocked;
};

const canActivateMember = (adminUser, target) => {
  if (!target || isImpersonationMode()) return false;
  if (isAdministrativeAccount(target)) return false;
  if (Number(adminUser?.id) === Number(target?.id)) return false;
  return isMemberArchived(target);
};

const canUnblockMember = (adminUser, target) => {
  if (!target || isImpersonationMode()) return false;
  if (isAdministrativeAccount(target)) return false;
  if (Number(adminUser?.id) === Number(target?.id)) return false;
  return !isMemberArchived(target) && Boolean(target?.is_blocked);
};

/** EMI auto-blocked members must reactivate via Account Recovery, not direct unblock. */
const memberRequiresRecoveryReactivation = (member) =>
  Boolean(member?.emi_auto_blocked);

const openAccountRecoveryForMember = (setSearchParams, searchParams, member) => {
  const next = new URLSearchParams(searchParams);
  next.set('tab', 'recovery');
  next.delete('member');
  next.delete('edit');
  if (member?.email) next.set('recovery_email', member.email);
  next.set('recovery_generate', '1');
  setSearchParams(next);
};

const canEditMember = (adminUser, target) => {
  if (!adminUser?.is_staff || isImpersonationMode()) return false;
  return !isAdministrativeAccount(target);
};

const memberDisplayName = (member) => {
  const full = [member?.first_name, member?.last_name].filter(Boolean).join(' ').trim();
  return full || member?.username || 'Member';
};

const MEMBER_JOINED_MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const memberJoinedYearOptions = () => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= current - 6; y -= 1) years.push(String(y));
  return years;
};

const formatJoinedMonthLabel = (year, month) => {
  if (!year && !month) return '';
  if (year && month) {
    const name = MEMBER_JOINED_MONTHS.find((m) => m.value === String(month))?.label || month;
    return `${name} ${year}`;
  }
  if (year) return String(year);
  return '';
};

const getMembersEmptyMessage = ({ search, statusFilter, joinedLabel }) => {
  if (search.trim()) {
    return {
      title: 'No members match the selected filter.',
      hint: 'Try a different name, username, or email address.',
    };
  }
  if (joinedLabel) {
    return {
      title: `No members joined in ${joinedLabel}.`,
      hint: 'Try another month, or clear the joined filter to see all members.',
    };
  }
  if (statusFilter === 'archived') {
    return {
      title: 'No archived members found.',
      hint: 'Members you move to archived will appear in this list.',
    };
  }
  return {
    title: 'No active members in this list.',
    hint: 'New sign-ups will appear here automatically.',
  };
};

const formatAdminDateTime = (value) => formatAppDateTime(value);

function ProfileDashboardCard({ title, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${className}`}>
      <p className="px-5 py-3.5 text-sm font-bold text-slate-800 bg-slate-50 border-b border-slate-100">{title}</p>
      <div className="p-5">{children}</div>
    </div>
  );
}

function ProfileStatCard({ label, value, className = 'border-slate-200 bg-slate-50' }) {
  return (
    <div className={`rounded-xl border p-4 ${className}`}>
      <p className="text-[11px] font-semibold text-slate-500 mb-1.5">{label}</p>
      <p className="text-xl font-black text-slate-900 leading-tight">{value}</p>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-900 text-left sm:text-right">{value}</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  ADMIN LOGIN
// ════════════════════════════════════════════════════════════════

function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!form.username || !form.password) { setError('Enter username and password.'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post(API.LOGIN, { username: form.username, password: form.password });
      const user = res.data.user;
      if (!user.is_staff && !user.is_superuser) {
        setError('Access denied. Admin account required.');
        setLoading(false);
        return;
      }
      // Store admin tokens; sync member tokens too (same JWT — avoids stale Edge/Chrome member refresh errors)
      clearMemberStorage();
      persistMemberTokens({ access: res.data.access, refresh: res.data.refresh });
      localStorage.setItem('admin_access_token', res.data.access);
      localStorage.setItem('admin_refresh_token', res.data.refresh);
      localStorage.setItem('admin_user', JSON.stringify(user));
      api.defaults.headers.common.Authorization = `Bearer ${res.data.access}`;
      onLogin(user);
    } catch (e) {
      const err = e.response?.data;
      setError(typeof err === 'object' ? Object.values(err).flat().join(' ') : 'Invalid credentials.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-5/12 bg-slate-900 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-black text-lg">S</span>
            </div>
            <span className="text-white font-black text-lg" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>SuccessFlow</span>
          </div>
          <h1 className="text-5xl font-black text-white leading-tight mb-5" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            Admin<br />Dashboard
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
            Manage members, process withdrawals, post community updates and track platform growth — all in one place.
          </p>
        </div>
        <div className="relative grid grid-cols-2 gap-3">
          {[
            { icon: '👥', label: 'Members', sub: 'View & bonus' },
            { icon: '🏦', label: 'Withdrawals', sub: 'Approve payouts' },
            { icon: '💳', label: 'Payments', sub: 'Proof & approve' },
            { icon: '📋', label: 'History', sub: 'Past payments' },
            { icon: '🛍️', label: 'Products', sub: 'Catalog' },
          ].map((f, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
              <span className="text-xl">{f.icon}</span>
              <p className="text-white text-xs font-bold mt-2">{f.label}</p>
              <p className="text-slate-500 text-[10px] mt-0.5">{f.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-9 h-9 bg-violet-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-black">S</span>
            </div>
            <span className="font-black text-slate-900 text-base" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>SuccessFlow Admin</span>
          </div>

          <h2 className="text-3xl font-black text-slate-900 mb-1" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Sign in</h2>
          <p className="text-slate-400 text-sm mb-8">Administrator access only</p>

          <div className="space-y-4">
            <FieldInput label="Username" placeholder="Admin username" value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus />
            <FieldInput label="Password" type="password" placeholder="Admin password" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <ErrBox msg={error} />
            <button onClick={handleLogin} disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold py-3.5 rounded-xl transition-colors">
              {loading ? 'Verifying…' : 'Sign In to Admin →'}
            </button>
          </div>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-xs font-bold text-amber-800 mb-2">🔑 Create admin account:</p>
            <code className="block text-[11px] bg-amber-100 px-3 py-2.5 rounded-lg font-mono text-amber-800">
              python manage.py createsuperuser
            </code>
          </div>

          <p className="text-center text-slate-400 text-xs mt-6">
            <a href="/" className="text-violet-500 hover:text-violet-700 font-semibold no-underline">← Back to SuccessFlow</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: OVERVIEW
// ════════════════════════════════════════════════════════════════

function OverviewTab({ stats, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon="👥" label="Total Users"         value={stats.total_users || 0} />
        <StatCard icon="✅" label="Active Members"      value={stats.active_members || 0}          colorClass="text-emerald-600" />
        <StatCard icon="💰" label="Total Revenue"       value={`₹${(stats.total_revenue || 0).toLocaleString('en-IN')}`} colorClass="text-violet-600" />
        <StatCard icon="⏳" label="Pending Withdrawals" value={stats.pending_withdrawals || 0}      colorClass="text-amber-600" />
        <StatCard icon="📤" label="Payout Awaiting"     value={`₹${(stats.pending_withdrawal_amount || 0).toLocaleString('en-IN')}`} colorClass="text-red-500" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm font-black text-slate-900">Recently Joined Members</p>
          <RefreshBtn onClick={onRefresh} />
        </div>
        {(!stats.recent_members || stats.recent_members.length === 0) ? (
          <div className="text-center py-16">
            <p className="text-5xl mb-3">👥</p>
            <p className="text-slate-500 font-semibold">No members have joined yet</p>
            <p className="text-slate-400 text-sm mt-1">New member sign-ups will appear here automatically</p>
          </div>
        ) : (
          <ScrollableListBody maxHeight="min(50vh, 420px)">
            {stats.recent_members.map(u => (
              <div key={u.id} className="flex items-center gap-4 px-6 py-3.5 transition-colors">
                <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                  {u.username[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <TruncateMemberCell
                    username={u.username}
                    email={u.email}
                    usernameClassName="text-sm font-semibold text-slate-800"
                    emailClassName="text-xs text-slate-400"
                  />
                </div>
                <RankBadge member={u} />
                <AccountStatusBadge member={u} />
                <span className="text-xs text-slate-400 hidden sm:block whitespace-nowrap">
                  {u.date_joined ? formatAppDate(u.date_joined) : '—'}
                </span>
              </div>
            ))}
          </ScrollableListBody>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  MEMBER PROFILE + EDIT (full-page views within admin panel)
// ════════════════════════════════════════════════════════════════

function MemberProfileView({ memberId, adminUser, onBack, onEdit, onOpenAccountRecovery }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [member, setMember] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: '', confirm_password: '' });
  const [resetPasswordErrors, setResetPasswordErrors] = useState({});

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadMember = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(API.ADMIN_USER_V2(memberId), { headers: adminHeaders() });
      setMember(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load member profile.');
    }
    setLoading(false);
  }, [memberId]);

  useEffect(() => { loadMember(); }, [loadMember]);

  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    if (!member) return;
    setSaving(true);
    setResetPasswordErrors({});
    try {
      await api.post(
        API.ADMIN_USER_RESET_PASSWORD(member.id),
        resetPasswordForm,
        { headers: adminHeaders() },
      );
      showToast('✅ Password updated. Share the new password with the member securely.');
      setShowResetPassword(false);
      setResetPasswordForm({ password: '', confirm_password: '' });
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.error) {
        setResetPasswordErrors(data);
      } else {
        showToast(`❌ ${data?.error || 'Failed to reset password.'}`);
      }
    }
    setSaving(false);
  };

  const handleArchive = async () => {
    if (!member) return;
    setSaving(true);
    try {
      await api.post(API.ADMIN_USER_ARCHIVE(member.id), {}, { headers: adminHeaders() });
      showToast(`📥 Archived ${member.username}`);
      setShowArchiveConfirm(false);
      await loadMember();
    } catch (err) {
      showToast(`❌ ${err.response?.data?.error || 'Failed to archive member.'}`);
    }
    setSaving(false);
  };

  const handleActivate = async () => {
    if (!member) return;
    setSaving(true);
    try {
      const res = await api.post(API.ADMIN_USER_RESTORE(member.id), {}, { headers: adminHeaders() });
      showToast(`✅ ${res.data?.message || `${member.username} has been activated.`}`);
      setShowActivateConfirm(false);
      await loadMember();
    } catch (err) {
      showToast(`❌ ${err.response?.data?.error || 'Failed to activate member.'}`);
    }
    setSaving(false);
  };

  const handleUnblock = async () => {
    if (!member) return;
    setSaving(true);
    try {
      const res = await api.post(
        API.ADMIN_BLOCK(member.id),
        { is_blocked: false },
        { headers: adminHeaders() },
      );
      showToast(`✅ ${res.data?.message || `${memberDisplayName(member)} has been reactivated.`}`);
      setShowUnblockConfirm(false);
      await loadMember();
    } catch (err) {
      showToast(`❌ ${err.response?.data?.error || 'Failed to unblock member.'}`);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-400 font-semibold">Loading member profile…</p>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={onBack} className="text-sm font-semibold text-violet-600 hover:text-violet-800">
          ← Back to members
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-700">{error || 'Member not found.'}</p>
        </div>
      </div>
    );
  }

  const showAdminActions = !isImpersonationMode() && adminUser?.is_staff && (
    canResetMemberPassword(adminUser, member)
    || canArchiveMember(adminUser, member)
    || canActivateMember(adminUser, member)
    || canUnblockMember(adminUser, member)
    || canEditMember(adminUser, member)
  );

  const availableBalance = Number(member.reward_points ?? 0);
  const displayName = memberDisplayName(member);

  return (
    <div className="space-y-5">
      <Toast msg={toast} />

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-800 transition-colors"
      >
        ← Back to member list
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-violet-600 flex items-center justify-center text-white text-xl font-black flex-shrink-0">
            {member.username?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-black text-slate-900">{displayName}</h2>
            <p className="text-sm text-slate-500 mt-0.5">@{member.username}</p>
          </div>
        </div>
        <AccountStatusBadge member={member} showDescription />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ProfileDashboardCard title="Member Information">
          <InfoRow label="Full Name" value={displayName} />
          <InfoRow label="Username" value={member.username} />
          <InfoRow label="Mobile Number" value={member.phone || '—'} />
          <InfoRow label="Email Address" value={member.email || '—'} />
          <InfoRow label="Date Joined" value={formatAdminDateTime(member.date_joined)} />
        </ProfileDashboardCard>

        <ProfileDashboardCard title="Membership Details">
          <InfoRow label="Current Plan" value={member.current_plan_name || '—'} />
          <InfoRow label="Membership Status" value={<MembershipBadge status={member.membership_status} />} />
          <InfoRow label="Plan Start Date" value={formatAdminDateTime(member.plan_joined_at)} />
          <InfoRow label="Plan End Date" value={formatAdminDateTime(member.plan_completed_at)} />
        </ProfileDashboardCard>
      </div>

      <ProfileDashboardCard
        title="Bank / UPI (payout details)"
        className={
          member.bank_details_ok
            ? 'border-emerald-200'
            : 'border-red-200 ring-1 ring-red-100'
        }
      >
        <div className="mb-3">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              member.bank_details_ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {member.bank_details_ok ? 'Updated — ready for payout' : 'Not updated — ask member to save in Profile'}
          </span>
          {member.bank_details_locked ? (
            <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
              Locked during payout
            </span>
          ) : null}
        </div>
        {!member.bank_details_ok ? (
          <p className="text-sm text-red-700 mb-3">
            Member has not saved UPI or bank yet. Ask them to save it in Profile. Then you can pay.
          </p>
        ) : null}
        <InfoRow label="Account holder" value={member.account_holder_name_as_per_bank || '—'} />
        <InfoRow label="Bank name" value={member.bank_name || '—'} />
        <InfoRow label="Account number" value={member.bank_account_number || '—'} />
        <InfoRow label="IFSC" value={member.bank_ifsc || '—'} />
        <InfoRow label="UPI ID" value={member.upi_id || '—'} />
      </ProfileDashboardCard>

      <ProfileDashboardCard title="Payments & Earnings">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProfileStatCard
            label="Reward Points"
            value={`⭐ ${Number(member.reward_points ?? 0).toLocaleString('en-IN')}`}
            className="border-amber-200 bg-amber-50"
          />
          <ProfileStatCard
            label="Total Earned"
            value={`⭐ ${Number(member.total_earned_points ?? 0).toLocaleString('en-IN')}`}
            className="border-violet-200 bg-violet-50"
          />
          <ProfileStatCard
            label="Total Withdrawn"
            value={`⭐ ${Number(member.total_withdrawn_points ?? 0).toLocaleString('en-IN')}`}
            className="border-slate-200 bg-slate-50"
          />
          <ProfileStatCard
            label="Available Balance"
            value={`⭐ ${availableBalance.toLocaleString('en-IN')}`}
            className="border-emerald-200 bg-emerald-50"
          />
        </div>
        <p className="text-sm text-slate-600 mt-4">
          These four numbers are the member wallet, not the bank transfer.
          Available / reward points stay here until you record a payout payment.
          Open Withdrawals → this member to see how much to send now (after 5% tax).
        </p>
      </ProfileDashboardCard>

      {showAdminActions && (
        <ProfileDashboardCard
          title="Admin Tools"
          className="border-violet-200 ring-1 ring-violet-100"
        >
          <p className="text-sm text-slate-600 mb-4">
            These tools help you support members. Each action will ask for confirmation before anything changes.
          </p>
          <div className="flex flex-wrap gap-3">
            {canResetMemberPassword(adminUser, member) && (
              <PrimaryBtn
                type="button"
                className="text-sm px-5 py-2.5"
                title={ADMIN_ACTION_TOOLTIPS.resetPassword}
                onClick={() => setShowResetPassword(true)}
              >
                Help Member Reset Password
              </PrimaryBtn>
            )}
            {canEditMember(adminUser, member) && (
              <GhostBtn
                type="button"
                className="text-sm px-5 py-2.5"
                title={ADMIN_ACTION_TOOLTIPS.updateDetails}
                onClick={onEdit}
              >
                Update Member Details
              </GhostBtn>
            )}
            {canArchiveMember(adminUser, member) && (
              <DangerBtn
                type="button"
                className="text-sm px-5 py-2.5"
                title={ADMIN_ACTION_TOOLTIPS.archive}
                onClick={() => setShowArchiveConfirm(true)}
              >
                Move to Archived Members
              </DangerBtn>
            )}
            {canActivateMember(adminUser, member) && (
              <PrimaryBtn
                type="button"
                className="text-sm px-5 py-2.5"
                title={ADMIN_ACTION_TOOLTIPS.restore}
                onClick={() => setShowActivateConfirm(true)}
              >
                Restore Member Access
              </PrimaryBtn>
            )}
            {canUnblockMember(adminUser, member) && (
              memberRequiresRecoveryReactivation(member) ? (
                <PrimaryBtn
                  type="button"
                  className="text-sm px-5 py-2.5"
                  title={ADMIN_ACTION_TOOLTIPS.reactivate}
                  onClick={() => onOpenAccountRecovery?.(member)}
                >
                  Open Account Recovery
                </PrimaryBtn>
              ) : (
                <PrimaryBtn
                  type="button"
                  className="text-sm px-5 py-2.5"
                  title={ADMIN_ACTION_TOOLTIPS.reactivate}
                  onClick={() => setShowUnblockConfirm(true)}
                >
                  Review & Reactivate Account
                </PrimaryBtn>
              )
            )}
          </div>
        </ProfileDashboardCard>
      )}

      {showResetPassword && (
        <Modal
          title="Help Member Access Their Account"
          subtitle={memberDisplayName(member)}
          onClose={() => { setShowResetPassword(false); setResetPasswordErrors({}); }}
        >
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Set a new temporary password for this member. Share the new password securely with the member after saving.
          </p>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <FieldInput label="Enter New Password" type="password" value={resetPasswordForm.password}
              onChange={e => setResetPasswordForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Enter new password" required autoComplete="new-password" />
            {resetPasswordErrors.password && (
              <p className="text-xs text-red-500">{resetPasswordErrors.password[0]}</p>
            )}
            <FieldInput label="Confirm Password" type="password" value={resetPasswordForm.confirm_password}
              onChange={e => setResetPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
              placeholder="Re-enter new password" required autoComplete="new-password" />
            {resetPasswordErrors.confirm_password && (
              <p className="text-xs text-red-500">{resetPasswordErrors.confirm_password[0]}</p>
            )}
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" type="button" onClick={() => { setShowResetPassword(false); setResetPasswordErrors({}); }}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save New Password'}
              </PrimaryBtn>
            </div>
          </form>
        </Modal>
      )}

      {showArchiveConfirm && (
        <Modal title="Archive Member?" subtitle={memberDisplayName(member)} onClose={() => setShowArchiveConfirm(false)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member will be removed from active member lists.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              All payment history, earnings, referrals, and transaction records will remain safely stored.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setShowArchiveConfirm(false)}>Cancel</GhostBtn>
              <DangerBtn className="flex-1" onClick={handleArchive} disabled={saving}>
                {saving ? 'Archiving…' : 'Move to Archived Members'}
              </DangerBtn>
            </div>
          </div>
        </Modal>
      )}

      {showActivateConfirm && (
        <Modal title="Restore Member Access?" subtitle={memberDisplayName(member)} onClose={() => setShowActivateConfirm(false)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member will be brought back into the active member list. The system will apply all existing restoration rules automatically.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setShowActivateConfirm(false)}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" onClick={handleActivate} disabled={saving}>
                {saving ? 'Restoring…' : 'Restore Member Access'}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}

      {showUnblockConfirm && (
        <Modal title="Restore member access?" subtitle={memberDisplayName(member)} onClose={() => setShowUnblockConfirm(false)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member was blocked manually by an admin (not for missed EMI payments). Confirm to restore login access.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setShowUnblockConfirm(false)}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" onClick={handleUnblock} disabled={saving}>
                {saving ? 'Processing…' : 'Restore Access'}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MemberEditView({ memberId, onBack, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadMember = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(API.ADMIN_USER_V2(memberId), { headers: adminHeaders() });
      const u = r.data;
      setMember(u);
      setForm({
        username: u.username || '',
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email || '',
        phone: u.phone || '',
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load member.');
    }
    setLoading(false);
  }, [memberId]);

  useEffect(() => { loadMember(); }, [loadMember]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setFormErrors({});
    try {
      await api.patch(
        API.ADMIN_USER_V2(memberId),
        {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
        },
        { headers: adminHeaders() },
      );
      showToast('✅ Member details updated.');
      onSaved?.();
    } catch (err) {
      if (err.response?.data) {
        setFormErrors(err.response.data);
        showToast('❌ Please check form errors.');
      } else {
        showToast('❌ Failed to save member.');
      }
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-400 font-semibold">Loading…</p>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={onBack} className="text-sm font-semibold text-violet-600 hover:text-violet-800">
          ← Back to profile
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-700">{error || 'Member not found.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Toast msg={toast} />

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-800 transition-colors"
      >
        ← Back to profile
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-black text-slate-900">Update Member Details</h2>
          <p className="text-xs text-slate-500 mt-1">Update contact and profile information for {memberDisplayName(member)}</p>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5 max-w-xl">
          <div>
            <Label>Username</Label>
            <p className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-sm font-semibold">
              {form.username}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldInput label="First Name" type="text" value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="John" />
              {formErrors.first_name && <p className="text-xs text-red-500 mt-1">{formErrors.first_name[0]}</p>}
            </div>
            <div>
              <FieldInput label="Last Name" type="text" value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Doe" />
              {formErrors.last_name && <p className="text-xs text-red-500 mt-1">{formErrors.last_name[0]}</p>}
            </div>
          </div>

          <div>
            <FieldInput label="Mobile Number" type="text" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="e.g. 9876543210" />
            <p className="text-[11px] text-slate-400 mt-1">Optional — primary contact for future MSG91 recovery.</p>
            {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone[0]}</p>}
          </div>

          <div>
            <FieldInput label="Email Address" type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
            {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email[0]}</p>}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <GhostBtn className="flex-1" type="button" onClick={onBack}>Cancel</GhostBtn>
            <PrimaryBtn className="flex-1" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </PrimaryBtn>
          </div>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: MEMBERS
// ════════════════════════════════════════════════════════════════

function MembersTab({ adminUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const memberId = searchParams.get('member');
  const editMemberId = searchParams.get('edit');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [meta, setMeta] = useState({ count: 0, total_pages: 1, has_next: false, has_previous: false });
  // const [modal, setModal] = useState(null);
  // const [bonus, setBonus] = useState({ points: '', note: '' });
  // const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [joinedYear, setJoinedYear] = useState('');
  const [joinedMonth, setJoinedMonth] = useState('');
  const [toast, setToast] = useState({ msg: '', variant: 'default' });
  const [actionSaving, setActionSaving] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [resetPasswordMember, setResetPasswordMember] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: '', confirm_password: '' });
  const [resetPasswordErrors, setResetPasswordErrors] = useState({});
  const joinedLabel = formatJoinedMonthLabel(joinedYear, joinedMonth);

  const openMemberProfile = (u) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'members');
    next.set('member', String(u.id));
    next.delete('edit');
    setSearchParams(next);
  };

  const closeMemberProfile = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('member');
    next.delete('edit');
    setSearchParams(next);
  };

  const openMemberEdit = () => {
    if (!memberId) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'members');
    next.set('member', memberId);
    next.set('edit', memberId);
    setSearchParams(next);
  };

  const closeMemberEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next);
  };

  const navigateToAccountRecovery = (member) => {
    openAccountRecoveryForMember(setSearchParams, searchParams, member);
  };

  const showToast = (msg, variant = 'default') => {
    setToast({ msg, variant });
    setTimeout(() => setToast({ msg: '', variant: 'default' }), 3500);
  };

  const memberLabel = (member) => member.username || `User #${member.id}`;

  const load = useCallback(async (pageNum = 1, searchTerm = '', overrides = null) => {
    setLoading(true); setError('');
    const f = overrides || {
      statusFilter,
      joinedYear,
      joinedMonth,
      search: searchTerm,
    };
    try {
      const params = {
        page: pageNum,
        page_size: pageSize,
        status: f.statusFilter ?? statusFilter,
      };
      const q = f.search !== undefined ? f.search : searchTerm;
      if (String(q || '').trim()) params.search = String(q).trim();
      const y = f.joinedYear !== undefined ? f.joinedYear : joinedYear;
      const m = f.joinedMonth !== undefined ? f.joinedMonth : joinedMonth;
      if (y) params.year = y;
      if (m) params.month = m;
      const r = await api.get(API.ADMIN_USERS, { headers: adminHeaders(), params });
      setUsers(asList(r.data));
      setMeta({
        count: r.data?.count ?? asList(r.data).length,
        total_pages: r.data?.total_pages ?? 1,
        has_next: Boolean(r.data?.has_next),
        has_previous: Boolean(r.data?.has_previous),
        page: r.data?.page ?? pageNum,
      });
      setPage(pageNum);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load members. Ensure you are logged in as admin.');
    }
    setLoading(false);
  }, [statusFilter, pageSize, joinedYear, joinedMonth]);

  const clearMemberFilters = () => {
    const cleared = {
      search: '',
      statusFilter: 'active',
      joinedYear: '',
      joinedMonth: '',
    };
    setSearch(cleared.search);
    setStatusFilter(cleared.statusFilter);
    setJoinedYear(cleared.joinedYear);
    setJoinedMonth(cleared.joinedMonth);
    load(1, '', cleared);
  };

  // Initial load + when leaving member profile/edit + page-size. Filters apply via Apply Filters only.
  useEffect(() => {
    if (memberId || editMemberId) return undefined;
    load(1, search);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- do not refetch on every filter keystroke
  }, [pageSize, memberId, editMemberId]);

  if (editMemberId) {
    return (
      <MemberEditView
        memberId={editMemberId}
        onBack={closeMemberEdit}
        onSaved={closeMemberEdit}
      />
    );
  }

  if (memberId) {
    return (
      <MemberProfileView
        memberId={memberId}
        adminUser={adminUser}
        onBack={closeMemberProfile}
        onEdit={openMemberEdit}
        onOpenAccountRecovery={navigateToAccountRecovery}
      />
    );
  }

  // const handleBonus = async () => {
  //   const pts = parseInt(bonus.points, 10);
  //   if (!pts || pts <= 0) { showToast('Enter valid points.', 'error'); return; }
  //   setSaving(true);
  //   try {
  //     await api.post(API.ADMIN_BONUS(modal.id), { points: pts, note: bonus.note || 'Admin bonus' }, { headers: adminHeaders() });
  //     showToast(`+${pts} pts added to ${modal.username} successfully.`, 'success');
  //     setModal(null); setBonus({ points: '', note: '' }); load(page, search);
  //   } catch (e) { showToast(e.response?.data?.error || 'Failed to add bonus.', 'error'); }
  //   setSaving(false);
  // };

  const handleBlockToggle = async (member) => {
    const nextBlocked = !member.is_blocked;
    const label = memberLabel(member);
    if (!nextBlocked && memberRequiresRecoveryReactivation(member)) {
      navigateToAccountRecovery(member);
      showToast(
        'This member must pay overdue EMIs via Account Recovery before access is restored.',
        'default',
      );
      return;
    }
    setActionSaving(member.id);
    try {
      const res = await api.post(
        API.ADMIN_BLOCK(member.id),
        { is_blocked: nextBlocked },
        { headers: adminHeaders() },
      );
      const confirmedBlocked = Boolean(res.data?.is_blocked ?? nextBlocked);
      setUsers((prev) => prev.map((u) => (u.id === member.id ? { ...u, is_blocked: confirmedBlocked } : u)));
      showToast(
        res.data?.message || `${label} has been ${nextBlocked ? 'blocked' : 'unblocked'} successfully.`,
        'success',
      );
    } catch (e) {
      showToast(
        e.response?.data?.error || `Failed to ${nextBlocked ? 'block' : 'unblock'} ${label}.`,
        'error',
      );
    }
    setActionSaving(null);
  };

  const handlePaymentUploadToggle = async (member) => {
    const label = memberLabel(member);
    const currentlyAllowed = member.payment_upload_effective !== false;
    const nextAllowed = !currentlyAllowed;
    setActionSaving(member.id);
    try {
      const res = await api.post(
        API.ADMIN_PAYMENT_UPLOAD(member.id),
        { payment_upload_allowed: nextAllowed },
        { headers: adminHeaders() },
      );
      setUsers((prev) => prev.map((u) => (
        u.id === member.id
          ? {
              ...u,
              payment_upload_allowed: res.data?.payment_upload_allowed ?? nextAllowed,
              payment_upload_override: res.data?.payment_upload_override,
              payment_upload_effective: res.data?.payment_upload_effective ?? nextAllowed,
            }
          : u
      )));
      showToast(
        res.data?.message
          || `Payment uploads ${nextAllowed ? 'enabled' : 'disabled'} for ${label}.`,
        'success',
      );
    } catch (e) {
      showToast(
        e.response?.data?.error
          || `Failed to update payment uploads for ${label}.`,
        'error',
      );
    }
    setActionSaving(null);
  };

  const handleArchiveMember = async (member) => {
    setActionSaving(member.id);
    try {
      await api.post(API.ADMIN_USER_ARCHIVE(member.id), {}, { headers: adminHeaders() });
      showToast(`${memberDisplayName(member)} has been archived.`, 'success');
      setConfirmAction(null);
      load(page, search);
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not archive this member. Please try again.', 'error');
    }
    setActionSaving(null);
  };

  const handleActivateMember = async (member) => {
    setActionSaving(member.id);
    try {
      const res = await api.post(API.ADMIN_USER_RESTORE(member.id), {}, { headers: adminHeaders() });
      showToast(res.data?.message || `${memberDisplayName(member)} has been activated.`, 'success');
      setConfirmAction(null);
      load(page, search);
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not activate this member. Please try again.', 'error');
    }
    setActionSaving(null);
  };

  const handleReactivateMember = (member) => {
    navigateToAccountRecovery(member);
    setConfirmAction(null);
    showToast(
      'Use Account Recovery to generate a payment link and approve catch-up payments.',
      'default',
    );
  };

  const handleResetPasswordFromList = async (e) => {
    if (e) e.preventDefault();
    if (!resetPasswordMember) return;
    setActionSaving(resetPasswordMember.id);
    setResetPasswordErrors({});
    try {
      await api.post(
        API.ADMIN_USER_RESET_PASSWORD(resetPasswordMember.id),
        resetPasswordForm,
        { headers: adminHeaders() },
      );
      showToast('Password updated. Share the new password with the member securely.', 'success');
      setResetPasswordMember(null);
      setResetPasswordForm({ password: '', confirm_password: '' });
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.error) {
        setResetPasswordErrors(data);
      } else {
        showToast(data?.error || 'Could not reset password. Please try again.', 'error');
      }
    }
    setActionSaving(null);
  };

  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 10 }).map((_, j) => (
        <td key={j} className="px-5 py-4">
          <div className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + (j * 13) % 50}%` }} />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="space-y-4">
      <Toast msg={toast.msg} variant={toast.variant} />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, username, or email…"
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-violet-400 transition" />
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shrink-0">
          {MEMBER_ACCOUNT_STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                statusFilter === opt.value
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
          <span className="font-semibold whitespace-nowrap">Joined</span>
          <select
            value={joinedMonth}
            onChange={(e) => {
              const nextMonth = e.target.value;
              setJoinedMonth(nextMonth);
              if (nextMonth && !joinedYear) {
                setJoinedYear(String(new Date().getFullYear()));
              }
            }}
            className="bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 px-2.5 py-2.5 outline-none focus:border-violet-400"
            aria-label="Joined month"
          >
            <option value="">All months</option>
            {MEMBER_JOINED_MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            value={joinedYear}
            onChange={(e) => setJoinedYear(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 px-2.5 py-2.5 outline-none focus:border-violet-400"
            aria-label="Joined year"
          >
            <option value="">All years</option>
            {memberJoinedYearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {(joinedYear || joinedMonth) ? (
            <button
              type="button"
              onClick={() => { setJoinedYear(''); setJoinedMonth(''); }}
              className="text-xs font-semibold text-violet-600 hover:underline px-1"
            >
              Clear
            </button>
          ) : null}
        </label>
        <AppButton
          type="button"
          variant="primary"
          icon="search"
          onClick={() => load(1, search)}
        >
          Apply Filters
        </AppButton>
        <AppButton type="button" variant="secondary" icon="x" onClick={clearMemberFilters}>
          Clear Filters
        </AppButton>
        <RefreshBtn onClick={() => load(page, search)} />
        <span className="text-xs text-slate-400 ml-auto font-semibold">
          {meta.count.toLocaleString('en-IN')} member{meta.count !== 1 ? 's' : ''}
          {joinedLabel ? ` joined in ${joinedLabel}` : ''}
          {meta.total_pages > 1 ? ` · page ${meta.page || page} / ${meta.total_pages}` : ''}
        </span>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm font-bold text-red-700 mb-1">Failed to load members</p>
          <p className="text-xs text-red-500">{error}</p>
          <button onClick={() => load(page, search)} className="text-xs text-red-600 font-semibold mt-2 hover:underline">Try again →</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <ScrollableTable
          stickyFirstColumn
          mobileScrollHint="Swipe sideways to view member details and admin tools"
          ariaLabel="Members table"
        >
          <table className="w-full text-sm" style={{ minWidth: '92rem' }} data-list-table>
            <thead>
              <tr>
                {['Member', 'Mobile', 'Referral Code', 'Rank', '⭐ Points', 'Referred By', 'Network', 'Membership', 'Account Status', 'Admin Tools'].map((h) => (
                  <th
                    key={h}
                    className={`text-left px-3 sm:px-5 py-3.5 whitespace-nowrap ${
                      h === 'Member' ? 'min-w-[14rem]' : ''
                    } ${h === 'Mobile' ? 'min-w-[8rem]' : ''} ${
                      h === 'Rank' ? 'min-w-[8rem]' : ''
                    } ${h === 'Membership' ? 'min-w-[9rem]' : ''} ${
                      h === 'Account Status' ? 'min-w-[9rem]' : ''
                    } ${h === 'Admin Tools' ? 'min-w-[14rem]' : ''}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              ) : users.length === 0 ? (
                <tr><td colSpan={10}>
                  <div className="text-center py-16 px-4">
                    <p className="text-5xl mb-3">👥</p>
                    <p className="text-slate-600 font-semibold text-base">
                      {getMembersEmptyMessage({ search, statusFilter, joinedLabel }).title}
                    </p>
                    <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
                      {getMembersEmptyMessage({ search, statusFilter, joinedLabel }).hint}
                    </p>
                    {(search || joinedYear || joinedMonth) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch('');
                          setJoinedYear('');
                          setJoinedMonth('');
                        }}
                        className="text-sm text-violet-600 mt-4 hover:underline font-semibold"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : users.map(u => {
                const rowName = memberDisplayName(u);
                const showUsernameLine = rowName !== u.username;
                return (
                <tr key={u.id} className="list-stripe-row transition-colors group">
                  <td className="px-5 py-4 align-middle">
                    <div className="relative z-[1] flex items-center gap-3 bg-inherit">
                      <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center text-white text-sm font-black flex-shrink-0">{u.username[0].toUpperCase()}</div>
                      <TruncateMemberCell
                        username={rowName}
                        email={showUsernameLine ? `@${u.username}` : (u.email || '—')}
                        onUsernameClick={() => openMemberProfile(u)}
                        usernameClassName="font-semibold text-slate-900 max-w-[140px]"
                        emailClassName="text-[11px] text-slate-700 max-w-[140px]"
                      />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-800 whitespace-nowrap align-middle">
                    <TruncateText text={u.phone || '—'} className="max-w-[8rem] text-xs font-semibold text-slate-800" />
                  </td>
                  <td className="px-5 py-4 align-middle"><code className="text-xs bg-slate-100 px-2.5 py-1 rounded-lg font-mono text-slate-800">{u.referral_code}</code></td>
                  <td className="px-5 py-4 min-w-[8rem] whitespace-nowrap align-middle">
                    <RankBadge member={u} />
                  </td>
                  <td className="px-5 py-4 align-middle whitespace-nowrap">
                    <span className="text-sm font-black text-amber-700">
                      ⭐ {Number(u.reward_points || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-700 max-w-[140px] align-middle">
                    <TruncateText text={u.referred_by_username || '—'} className="max-w-[140px] text-xs font-medium text-slate-700" />
                  </td>
                  <td className="px-5 py-4 align-middle"><span className="text-sm font-black text-violet-700">{u.referral_count || 0}</span></td>
                  <td className="px-5 py-4 align-middle whitespace-nowrap"><MembershipBadge status={u.membership_status} /></td>
                  <td className="px-5 py-4 align-middle whitespace-nowrap">
                    <AccountStatusBadge member={u} />
                  </td>
                  <td className="px-5 py-4 pr-6 align-middle whitespace-nowrap">
                    <div className="inline-flex items-center gap-2 flex-nowrap">
                      <button
                        type="button"
                        title="View member details"
                        aria-label="View member details"
                        onClick={() => openMemberProfile(u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-violet-600 shrink-0"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {!u.is_staff && !u.is_superuser && (
                        <>
                          <button
                            type="button"
                            title={
                              u.payment_upload_effective === false
                                ? 'Uploads are off — click to enable payment uploads'
                                : 'Uploads are on — click to disable payment uploads'
                            }
                            aria-label={
                              u.payment_upload_effective === false
                                ? 'Enable payment uploads'
                                : 'Disable payment uploads'
                            }
                            disabled={actionSaving === u.id}
                            onClick={() => handlePaymentUploadToggle(u)}
                            className={`inline-flex h-9 items-center justify-center rounded-xl border px-2 text-[10px] font-bold uppercase tracking-wide transition-colors disabled:opacity-50 shrink-0 ${
                              u.payment_upload_effective === false
                                ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                                : 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100'
                            }`}
                          >
                            {u.payment_upload_effective === false ? 'Enable Uploads' : 'Disable Uploads'}
                          </button>
                          <button
                            type="button"
                            title={u.is_blocked ? 'Unblock member' : 'Block member'}
                            aria-label={u.is_blocked ? 'Unblock member' : 'Block member'}
                            disabled={actionSaving === u.id}
                            onClick={() => handleBlockToggle(u)}
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border font-semibold transition-colors disabled:opacity-50 shrink-0 ${
                              u.is_blocked
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                            }`}
                          >
                            {actionSaving === u.id ? (
                              <span className="text-xs">…</span>
                            ) : u.is_blocked ? (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4l16 16" />
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </ScrollableTable>
        <TablePagination
          page={meta.page || page}
          totalPages={meta.total_pages}
          onPageChange={(p) => load(p, search)}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={meta.count || users.length}
          rowsPerPageId="admin-members-rows-per-page"
          embedded
          className="px-5"
        />
      </div>

      {/* Add Bonus Points — commented out for now
      {modal && (
        <Modal title="Add Bonus Points" subtitle={`Adding reward points for ${memberDisplayName(modal)}`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-100 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center text-white font-black">{modal.username[0].toUpperCase()}</div>
              <div>
                <p className="text-sm font-bold text-slate-900">{memberDisplayName(modal)}</p>
                <p className="text-xs text-slate-500">Current balance: ⭐ {(modal.reward_points || 0).toLocaleString('en-IN')} points</p>
              </div>
            </div>
            <FieldInput label="Points to Add" type="number" value={bonus.points}
              onChange={e => setBonus(b => ({ ...b, points: e.target.value }))} placeholder="e.g. 100" min="1" />
            <FieldTextarea label="Reason (optional)" value={bonus.note}
              onChange={e => setBonus(b => ({ ...b, note: e.target.value }))} placeholder="Why are you adding these points?" />
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setModal(null)}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" onClick={handleBonus} disabled={saving}>
                {saving ? 'Adding…' : `Add ${bonus.points || '0'} Points`}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}
      */}

      {resetPasswordMember && (
        <Modal
          title="Help Member Access Their Account"
          subtitle={memberDisplayName(resetPasswordMember)}
          onClose={() => { setResetPasswordMember(null); setResetPasswordErrors({}); }}
        >
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Set a new temporary password for this member. Share the new password securely with the member after saving.
          </p>
          <form onSubmit={handleResetPasswordFromList} className="space-y-4">
            <FieldInput label="Enter New Password" type="password" value={resetPasswordForm.password}
              onChange={e => setResetPasswordForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Enter new password" required autoComplete="new-password" />
            {resetPasswordErrors.password && (
              <p className="text-xs text-red-500">{resetPasswordErrors.password[0]}</p>
            )}
            <FieldInput label="Confirm Password" type="password" value={resetPasswordForm.confirm_password}
              onChange={e => setResetPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
              placeholder="Re-enter new password" required autoComplete="new-password" />
            {resetPasswordErrors.confirm_password && (
              <p className="text-xs text-red-500">{resetPasswordErrors.confirm_password[0]}</p>
            )}
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" type="button" onClick={() => { setResetPasswordMember(null); setResetPasswordErrors({}); }}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" type="submit" disabled={actionSaving === resetPasswordMember.id}>
                {actionSaving === resetPasswordMember.id ? 'Saving…' : 'Save New Password'}
              </PrimaryBtn>
            </div>
          </form>
        </Modal>
      )}

      {confirmAction?.type === 'archive' && (
        <Modal
          title="Archive Member?"
          subtitle={memberDisplayName(confirmAction.member)}
          onClose={() => setConfirmAction(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member will be removed from active member lists.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              All payment history, earnings, referrals, and transaction records will remain safely stored.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setConfirmAction(null)}>Cancel</GhostBtn>
              <DangerBtn
                className="flex-1"
                onClick={() => handleArchiveMember(confirmAction.member)}
                disabled={actionSaving === confirmAction.member.id}
              >
                {actionSaving === confirmAction.member.id ? 'Archiving…' : 'Move to Archived Members'}
              </DangerBtn>
            </div>
          </div>
        </Modal>
      )}

      {confirmAction?.type === 'activate' && (
        <Modal
          title="Restore Member Access?"
          subtitle={memberDisplayName(confirmAction.member)}
          onClose={() => setConfirmAction(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member will be brought back into the active member list. The system will apply all existing restoration rules automatically.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setConfirmAction(null)}>Cancel</GhostBtn>
              <PrimaryBtn
                className="flex-1"
                onClick={() => handleActivateMember(confirmAction.member)}
                disabled={actionSaving === confirmAction.member.id}
              >
                {actionSaving === confirmAction.member.id ? 'Restoring…' : 'Restore Member Access'}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}

      {confirmAction?.type === 'reactivate' && (
        <Modal
          title="Account Recovery required"
          subtitle={memberDisplayName(confirmAction.member)}
          onClose={() => setConfirmAction(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              This member was auto-blocked for missed EMI payments. Generate a recovery link, review their payment proof, and approve to unblock.
            </p>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setConfirmAction(null)}>Cancel</GhostBtn>
              <PrimaryBtn
                className="flex-1"
                onClick={() => handleReactivateMember(confirmAction.member)}
              >
                Open Account Recovery
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: PAYMENTS (plan / EMI proof approvals)
// ════════════════════════════════════════════════════════════════

const IconEye = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const IconCheck = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const IconX = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function PaymentActionButton({ title, onClick, disabled, className, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [awaitingUpload, setAwaitingUpload] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState({ msg: '', variant: 'default' });
  const [mediaPreview, setMediaPreview] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [approveSubmittingId, setApproveSubmittingId] = useState(null);
  const [approveConfirmRow, setApproveConfirmRow] = useState(null);
  const [auditRow, setAuditRow] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [revertReason, setRevertReason] = useState('');
  const [revertSubmitting, setRevertSubmitting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const toastTimerRef = useRef(null);

  const showToast = (msg, variant = 'default') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, variant });
    toastTimerRef.current = setTimeout(() => {
      setToast({ msg: '', variant: 'default' });
      toastTimerRef.current = null;
    }, 3800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(API.ADMIN_USER_PAYMENTS, {
        params: { page, page_size: pageSize },
        headers: adminHeaders(),
      });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setRows(parsed.items.map(normalizePaymentQueueRow));
      setAwaitingUpload(
        Array.isArray(r.data?.awaiting_upload) ? r.data.awaiting_upload : []
      );
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (e) {
      setRows([]);
      setAwaitingUpload([]);
      setTotalPages(1);
      setTotalCount(0);
      setError(e.response?.data?.error || 'Failed to load payment queue. Check admin login and try again.');
    }
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((u) =>
    (u.username || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.current_plan || '').toLowerCase().includes(search.toLowerCase())
  );

  const fmtMoney = (n) => {
    const num = Number(n ?? 0);
    if (!Number.isFinite(num)) return String(n ?? '—');
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const openApproveConfirm = (u) => {
    if (!u.can_approve) {
      showToast(u.payment_validation?.approval_blocked_reason || 'This payment cannot be approved.', 'error');
      return;
    }
    setApproveConfirmRow(u);
  };

  const closeApproveConfirm = () => {
    if (approveSubmittingId) return;
    setApproveConfirmRow(null);
  };

  const handleApprove = async (u) => {
    const id = u.payment_proof_id ?? u.id;
    setApproveSubmittingId(id);
    try {
      const res = await api.post(API.ADMIN_PAYMENT_APPROVE(id), {}, { headers: adminHeaders() });
      if (res.data?.success) {
        showToast(res.data.message || 'Payment approved successfully.', 'success');
        setApproveConfirmRow(null);
        setRows((prev) => prev.filter((row) => (row.payment_proof_id ?? row.id) !== id));
        load();
      } else {
        showToast(
          res.data?.message || res.data?.error || 'Failed to approve payment.',
          'error'
        );
      }
    } catch (e) {
      showToast(
        e.response?.data?.message ||
          e.response?.data?.error ||
          e.response?.data?.detail ||
          'Failed to approve payment.',
        'error'
      );
    } finally {
      setApproveSubmittingId(null);
    }
  };

  const openRejectDialog = (u) => {
    setRejectNote('');
    setRejectRow(u);
  };

  const closeRejectDialog = () => {
    if (rejectSubmitting) return;
    setRejectRow(null);
    setRejectNote('');
  };

  const handleRejectSubmit = async () => {
    if (!rejectRow) return;
    const note = rejectNote.trim();
    if (note.length < 5) {
      showToast('Rejection reason must be at least 5 characters.', 'error');
      return;
    }
    const id = rejectRow.payment_proof_id ?? rejectRow.id;
    setRejectSubmitting(true);
    try {
      const res = await api.post(
        API.ADMIN_PAYMENT_REJECT(id),
        { admin_note: note },
        { headers: adminHeaders() }
      );
      if (res.data?.success) {
        showToast(res.data.message || 'Successfully updated the payment status.', 'success');
        setRejectRow(null);
        setRejectNote('');
        load();
      } else {
        showToast(
          res.data?.message || res.data?.error || 'Failed to reject payment.',
          'error'
        );
      }
    } catch (e) {
      showToast(
        e.response?.data?.message ||
          e.response?.data?.error ||
          e.response?.data?.detail ||
          'Failed to reject payment.',
        'error'
      );
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleRevertApproval = async () => {
    if (!auditRow) return;
    const reason = revertReason.trim();
    if (reason.length < 10) {
      showToast('Please write what went wrong (at least 10 letters).', 'error');
      return;
    }
    const id = auditRow.payment_proof_id ?? auditRow.id;
    setRevertSubmitting(true);
    try {
      const res = await api.post(
        API.ADMIN_PAYMENT_REVERT(id),
        { reason },
        { headers: adminHeaders() }
      );
      if (res.data?.success) {
        showToast(res.data.message || 'Done. Payment is back on the Waiting list.', 'success');
        setAuditRow(null);
        setAuditData(null);
        setRevertReason('');
        setShowRevertConfirm(false);
        load();
      } else {
        showToast(res.data?.error || 'Could not change this payment. Please try again.', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not change this payment. Please try again.', 'error');
    } finally {
      setRevertSubmitting(false);
    }
  };

  const PaySkeletonRow = () => (
    <tr>
      {Array.from({ length: 8 }).map((_, j) => (
        <td key={j} className="px-5 py-4">
          <div className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + (j * 11) % 45}%` }} />
        </td>
      ))}
    </tr>
  );

  const viewPaymentProof = (u) => {
    const result = handleMediaView(u.payment_proof, {
      title: 'Payment proof',
      subtitle: u.username,
      onPreview: setMediaPreview,
    });
    if (!result.ok) showToast('No payment proof uploaded.', 'error');
    else if (result.kind === 'document') showToast('Opening document in a new tab…', 'default');
  };

  const openPaymentAudit = async (u) => {
    const id = u.payment_proof_id ?? u.id;
    setAuditRow(u);
    setAuditData(null);
    setRevertReason('');
    setAuditLoading(true);
    try {
      const res = await api.get(API.ADMIN_PAYMENT_AUDIT(id), { headers: adminHeaders() });
      if (res.data?.success) {
        setAuditData(res.data);
      } else {
        showToast(res.data?.error || 'Could not load payment audit trail.', 'error');
        setAuditRow(null);
      }
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not load payment audit trail.', 'error');
      setAuditRow(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const formatAuditTime = (iso) => formatAppDateTime(iso);

  return (
    <div className="space-y-4">
      <Toast msg={toast.msg} variant={toast.variant} />

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="font-semibold text-slate-700">Plan types:</span>
        <AdminPlanTypeBadge isFullSettlement={false} />
        <AdminPlanTypeBadge isFullSettlement />
        <span className="text-slate-500">Use these labels when approving or rejecting payments.</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-3 text-xs text-indigo-950">
        <span>After you mark a payment as Paid or Rejected, it moves to <strong>Payment History</strong>. If you clicked Paid by mistake, open it there and use <strong>Revert back to Waiting list</strong>.</span>
        <a
          href="/admin-panel?tab=payment-history"
          className="shrink-0 font-bold text-violet-700 hover:text-violet-900 no-underline"
        >
          Open Payment History →
        </a>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50 text-red-900">
          <span className="text-lg flex-shrink-0" aria-hidden>⚠️</span>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {awaitingUpload.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-900">
              Awaiting payment upload ({awaitingUpload.length})
            </p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Enrolled in a plan but no screenshot in the approval queue yet. The member must
              upload from Payments before you can approve or reject.
            </p>
          </div>
          <ul className="divide-y divide-slate-200">
            {awaitingUpload.map((item) => {
              const u = item.user_details || item.user || {};
              const planMeta = resolveAdminPlanTypeMeta(item);
              return (
                <li key={item.user_plan_id ?? u.id} className="flex items-center gap-4 px-5 py-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                    {(u.username || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <TruncateMemberCell
                      username={u.username}
                      email={u.email}
                      usernameClassName="text-sm font-semibold text-slate-900"
                      emailClassName="text-xs text-slate-500"
                    />
                  </div>
                  <AdminPlanTypeBadge
                    isFullSettlement={planMeta.isFullSettlement}
                    label={planMeta.label}
                  />
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600">
                    No proof yet
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or plan…"
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-violet-400 transition"
          />
        </div>
        <RefreshBtn onClick={load} />
        <span className="text-xs text-slate-400 ml-auto font-semibold">
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <ScrollableTable
          stickyFirstColumn
          mobileScrollHint="Swipe horizontally — plan type sits beside Approve / Reject"
          ariaLabel="Pending payments table"
        >
          <table className="w-full text-sm" style={{ minWidth: '1020px' }} data-list-table>
            <thead>
              <tr>
                {['Name', 'Amount', 'Validation', 'View proof', 'Paid', 'Remaining', 'Plan', 'Actions'].map((h) => (
                  <th key={h} className="text-left px-5 py-3.5 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <PaySkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="text-center py-16">
                      <p className="text-5xl mb-3">💳</p>
                      <p className="text-slate-500 font-semibold">{search ? 'No matches' : 'No payment records'}</p>
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch('')}
                          className="text-xs text-violet-600 mt-2 hover:underline"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u.payment_proof_id ?? u.id} className="list-stripe-row transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                          {(u.username || '?')[0].toUpperCase()}
                        </div>
                        <TruncateMemberCell username={u.username} email={u.email} />
                      </div>
                    </td>
                    <td className="px-5 py-4 font-black text-slate-900 whitespace-nowrap">{fmtMoney(u.current_payment)}</td>
                    <td className="px-5 py-4">
                      <div className="space-y-1">
                        <PaymentValidationBadge validation={u.payment_validation} />
                        {u.was_approval_reverted && String(u.proof_status || '').toUpperCase() === 'PENDING' && (
                          <span className="block text-[10px] font-bold text-amber-800">Sent back once</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <PaymentActionButton
                        title="View payment proof"
                        onClick={() => { setDetailRow(null); viewPaymentProof(u); }}
                        className="border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-violet-200 hover:text-violet-600"
                      >
                        <IconEye />
                      </PaymentActionButton>
                    </td>
                    <td className="px-5 py-4 text-xs font-semibold text-emerald-700 whitespace-nowrap">
                      {fmtMoney(u.total_paid)}
                    </td>
                    <td className="px-5 py-4 text-xs font-semibold text-violet-700 whitespace-nowrap">
                      {fmtMoney(u.remaining)}
                    </td>
                    <td className="px-5 py-4">
                      <AdminPlanTypeBadge
                        isFullSettlement={u.plan_is_full_settlement}
                        label={u.current_plan}
                      />
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap align-middle">
                      <div className="inline-flex flex-nowrap items-center gap-1.5">
                        <PaymentActionButton
                          title="View payment audit trail"
                          onClick={() => openPaymentAudit(u)}
                          className="border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-amber-200 hover:text-amber-700"
                        >
                          <span className="text-[10px] font-black">Log</span>
                        </PaymentActionButton>
                        <PaymentActionButton
                          title="View member details"
                          onClick={() => { setMediaPreview(null); setDetailRow(u); }}
                          className="border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-violet-200 hover:text-violet-600"
                        >
                          <IconEye />
                        </PaymentActionButton>
                        <PaymentActionButton
                          title={u.can_approve ? 'Approve' : (u.payment_validation?.approval_blocked_reason || 'Approval blocked')}
                          onClick={() => openApproveConfirm(u)}
                          disabled={!u.can_approve || approveSubmittingId === (u.payment_proof_id ?? u.id)}
                          className={`border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 ${!u.can_approve ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <IconCheck />
                        </PaymentActionButton>
                        <PaymentActionButton
                          title="Reject"
                          onClick={() => openRejectDialog(u)}
                          disabled={rejectSubmitting}
                          className="border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300"
                        >
                          <IconX />
                        </PaymentActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollableTable>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={totalCount}
          rowsPerPageId="admin-payments-rows-per-page"
          embedded
          className="px-5"
        />
      </div>

      <MediaPreviewModal
        open={Boolean(mediaPreview)}
        src={mediaPreview?.src}
        title={mediaPreview?.title}
        subtitle={mediaPreview?.subtitle}
        onClose={() => setMediaPreview(null)}
      />

      {detailRow && (
        <Modal
          title="Payment & member details"
          subtitle={
            <span className="inline-flex flex-wrap items-center gap-2">
              <span>{detailRow.username}</span>
              <AdminPlanTypeBadge
                isFullSettlement={detailRow.plan_is_full_settlement}
                label={detailRow.current_plan}
              />
            </span>
          }
          onClose={() => setDetailRow(null)}
          largeClose
          maxWidthClass="max-w-lg"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-100 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                {(detailRow.username || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <TruncateMemberCell
                  username={detailRow.username}
                  email={detailRow.email}
                  usernameClassName="text-sm font-bold text-slate-900"
                  emailClassName="text-xs text-slate-500"
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  {fmtMoney(detailRow.current_payment)} this payment · {fmtMoney(detailRow.total_paid)} paid ·{' '}
                  <span className="font-semibold text-violet-700">{fmtMoney(detailRow.remaining)}</span> remaining
                </p>
              </div>
            </div>

            <PaymentValidationSummary row={detailRow} fmtMoney={fmtMoney} />

            <div className="max-h-[min(52vh,420px)] overflow-y-auto rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {ADMIN_PAYMENT_DETAIL_FIELDS.filter(
                ([key]) => key !== 'username' && key !== 'email' && key !== 'payment_proof'
              ).map(([key, label]) => (
                <DetailKvRow
                  key={key}
                  label={label}
                  value={
                    key === 'current_plan' ? (
                      <AdminPlanTypeBadge
                        isFullSettlement={detailRow.plan_is_full_settlement}
                        label={detailRow.current_plan}
                      />
                    ) : (
                      formatPaymentDetailValue(key, detailRow[key])
                    )
                  }
                />
              ))}
            </div>
          </div>
        </Modal>
      )}

      {auditRow && (
        <Modal
          title="Payment audit trail"
          subtitle={`${auditRow.username} · proof #${auditRow.payment_proof_id ?? auditRow.id}`}
          onClose={() => { setAuditRow(null); setAuditData(null); setRevertReason(''); setShowRevertConfirm(false); }}
          largeClose
          maxWidthClass="max-w-2xl"
        >
          {auditLoading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading audit history…</p>
          ) : (
            <div className="space-y-4">
              {auditData?.payment_validation && (
                <PaymentValidationSummary
                  row={{ payment_validation: auditData.payment_validation, was_approval_reverted: auditData.was_approval_reverted }}
                  fmtMoney={fmtMoney}
                />
              )}

              {auditData?.can_revert_approval && (
                <AdminUndoApprovalPanel
                  phase={showRevertConfirm ? 'confirm' : 'intro'}
                  revertReason={revertReason}
                  onRevertReasonChange={setRevertReason}
                  onStartConfirm={() => setShowRevertConfirm(true)}
                  onCancelConfirm={() => { setShowRevertConfirm(false); setRevertReason(''); }}
                  onSubmit={handleRevertApproval}
                  submitting={revertSubmitting}
                />
              )}

              {auditData?.payment_attempt && (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-xs text-slate-700">
                  <p className="font-bold text-slate-900 mb-1">Payment attempt session</p>
                  <p>Status: <strong>{auditData.payment_attempt.status}</strong></p>
                  <p>Session ID: <code className="text-[10px]">{auditData.payment_attempt.session_id}</code></p>
                  <p>Expected: {auditData.payment_attempt.expected_amount} {auditData.payment_attempt.currency}</p>
                </div>
              )}

              <div className="max-h-[min(50vh,420px)] overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                {(auditData?.attempt_timeline?.length
                  ? auditData.attempt_timeline
                  : auditData?.session_audit_logs?.length
                    ? auditData.session_audit_logs
                    : auditData?.audit_logs || []
                ).map((entry, idx) => (
                  <div key={`${entry.action}-${entry.created_at}-${idx}`} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {entry.action_label || entry.action}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {entry.actor ? `By ${entry.actor}` : 'System / member'}
                          {entry.metadata?.event ? ` · ${entry.metadata.event}` : ''}
                        </p>
                        {entry.metadata?.failure_reason && (
                          <p className="text-[11px] text-red-700 mt-1">{entry.metadata.failure_reason}</p>
                        )}
                        {entry.metadata?.admin_note && (
                          <p className="text-[11px] text-amber-800 mt-1">Note: {entry.metadata.admin_note}</p>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {formatAuditTime(entry.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
                {!auditData?.attempt_timeline?.length &&
                  !auditData?.session_audit_logs?.length &&
                  !auditData?.audit_logs?.length && (
                    <p className="text-sm text-slate-500 py-8 text-center">No audit events recorded yet.</p>
                  )}
              </div>
            </div>
          )}
        </Modal>
      )}

      {approveConfirmRow && (
        <Modal
          title="Confirm payment approval"
          subtitle={`Review before approving ${approveConfirmRow.username}`}
          onClose={closeApproveConfirm}
          maxWidthClass="max-w-md"
        >
          <div className="space-y-4">
            <PaymentValidationSummary row={approveConfirmRow} fmtMoney={fmtMoney} />
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs space-y-1">
              <p><span className="text-slate-500">Customer:</span> <strong>{approveConfirmRow.username}</strong></p>
              <p><span className="text-slate-500">Plan:</span>{' '}
                <AdminPlanTypeBadge
                  isFullSettlement={approveConfirmRow.plan_is_full_settlement}
                  label={approveConfirmRow.current_plan}
                />
              </p>
              <p><span className="text-slate-500">Outstanding balance:</span> <strong>{fmtMoney(approveConfirmRow.remaining)}</strong></p>
            </div>
            <p className="text-xs text-slate-600 leading-snug">
              Only approve if the uploaded proof matches the expected amount for this plan.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closeApproveConfirm}
                disabled={Boolean(approveSubmittingId)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleApprove(approveConfirmRow)}
                disabled={Boolean(approveSubmittingId) || !approveConfirmRow.can_approve}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approveSubmittingId ? 'Approving…' : 'Confirm approve'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {rejectRow && (
        <Modal
          title="Reject payment"
          subtitle={rejectRow.username ? `Payment for ${rejectRow.username}` : 'Provide a reason for rejection'}
          onClose={closeRejectDialog}
          maxWidthClass="max-w-md"
        >
          <div className="space-y-4">
            <FieldTextarea
              label="Rejection reason"
              placeholder="e.g. Screenshot is unclear. Please re-upload a full image."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={4}
            />
            <p className="text-[11px] text-slate-400">Minimum 5 characters. The member may see this note.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closeRejectDialog}
                disabled={rejectSubmitting}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                disabled={rejectSubmitting || rejectNote.trim().length < 5}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rejectSubmitting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: PAYMENT HISTORY (archive + undo approval)
// ════════════════════════════════════════════════════════════════

function paymentReviewedByLabel(row) {
  const status = String(row.proof_status || '').toUpperCase();
  if (status === 'PENDING') return '—';
  return row.reviewed_by_username || '—';
}

function PaymentHistoryTab() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mismatchCount, setMismatchCount] = useState(0);
  const [toast, setToast] = useState({ msg: '', variant: 'default' });
  const [mediaPreview, setMediaPreview] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [revertSubmitting, setRevertSubmitting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const toastTimerRef = useRef(null);

  const showToast = (msg, variant = 'default') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, variant });
    toastTimerRef.current = setTimeout(() => {
      setToast({ msg: '', variant: 'default' });
      toastTimerRef.current = null;
    }, 3800);
  };

  const fmtMoney = (n) => {
    const num = Number(n ?? 0);
    if (!Number.isFinite(num)) return String(n ?? '—');
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const load = useCallback(async (pageNum = 1, overrides = null) => {
    setLoading(true);
    setError('');
    const f = overrides || { search, statusFilter };
    try {
      const historyParams = {
        page: pageNum,
        page_size: pageSize,
        search: String(f.search || '').trim() || undefined,
      };
      const status = f.statusFilter !== undefined ? f.statusFilter : statusFilter;
      if (status === 'amount_mismatch') {
        historyParams.status = 'approved';
        historyParams.amount_mismatch = '1';
      } else {
        historyParams.status = status;
      }
      const r = await api.get(API.ADMIN_PAYMENTS_HISTORY, {
        params: historyParams,
        headers: adminHeaders(),
      });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setRows(parsed.items.map(normalizePaymentQueueRow));
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
      setMismatchCount(Number(r.data?.amount_mismatch_count ?? 0));
    } catch (e) {
      setRows([]);
      setTotalPages(1);
      setTotalCount(0);
      setError(e.response?.data?.error || 'Could not load payment history.');
    }
    setLoading(false);
  }, [pageSize, statusFilter, search]);

  const clearPaymentHistoryFilters = () => {
    const cleared = { search: '', statusFilter: 'all' };
    setSearch(cleared.search);
    setStatusFilter(cleared.statusFilter);
    load(1, cleared);
  };

  // Initial + page-size only. Status / search apply via Apply Filters.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional Apply Filters pattern
  }, [pageSize]);

  const viewPaymentProof = (u) => {
    const result = handleMediaView(u.payment_proof, {
      title: 'Payment proof',
      subtitle: `${u.username} · ${u.transaction_id || 'payment'}`,
      onPreview: setMediaPreview,
    });
    if (!result.ok) showToast('No payment screenshot saved for this record.', 'error');
    else if (result.kind === 'pdf') showToast('Opening PDF in a new tab…', 'default');
  };

  const openDetails = async (u) => {
    setDetailRow(u);
    setAuditData(null);
    setRevertReason('');
    setShowRevertConfirm(false);
    setAuditLoading(true);
    const id = u.payment_proof_id ?? u.id;
    try {
      const res = await api.get(API.ADMIN_PAYMENT_AUDIT(id), { headers: adminHeaders() });
      if (res.data?.success) {
        setAuditData(res.data);
      } else {
        showToast(res.data?.error || 'Could not load payment details.', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not load payment details.', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  const closeDetails = () => {
    if (revertSubmitting) return;
    setDetailRow(null);
    setAuditData(null);
    setRevertReason('');
    setShowRevertConfirm(false);
  };

  const handleRevertApproval = async () => {
    if (!detailRow) return;
    const reason = revertReason.trim();
    if (reason.length < 10) {
      showToast('Please write what went wrong (at least 10 letters).', 'error');
      return;
    }
    const id = detailRow.payment_proof_id ?? detailRow.id;
    setRevertSubmitting(true);
    try {
      const res = await api.post(
        API.ADMIN_PAYMENT_REVERT(id),
        { reason },
        { headers: adminHeaders() }
      );
      if (res.data?.success) {
        showToast(res.data.message || 'Done. Payment is back on the Waiting list.', 'success');
        closeDetails();
        load(page);
      } else {
        showToast(res.data?.error || 'Could not change this payment. Please try again.', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not change this payment. Please try again.', 'error');
    } finally {
      setRevertSubmitting(false);
    }
  };

  const HistorySkeletonRow = () => (
    <tr>
      {Array.from({ length: 8 }).map((_, j) => (
        <td key={j} className="px-5 py-4">
          <div className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${35 + (j * 13) % 50}%` }} />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="space-y-4">
      {toast.msg ? (
        <div className={`fixed top-5 right-5 z-[200] max-w-sm px-5 py-3 rounded-2xl shadow-2xl text-sm font-semibold ${
          toast.variant === 'error' ? 'bg-red-600 text-white' : toast.variant === 'success' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'
        }`}>
          {toast.msg}
        </div>
      ) : null}

      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50/90 via-white to-violet-50/80 p-4 sm:p-5">
        <p className="text-sm font-bold text-slate-900">Old payments — search &amp; fix mistakes</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed max-w-3xl">
          All member payments are saved here — Paid, Rejected, or still Waiting.
          Tap a row to see the payment photo and details.
          Wrongly marked as <strong>Paid</strong>? Open it and tap <strong>Revert back to Waiting list</strong> (one time only).
          Rows with an orange <strong>Underpaid approval</strong> badge were paid at the wrong amount for the plan — revert, collect the correct amount, then approve again.
        </p>
        {mismatchCount > 0 ? (
          <p className="text-xs font-bold text-orange-900 mt-2">
            {mismatchCount} approved payment{mismatchCount === 1 ? '' : 's'} need amount review — use the <strong>Underpaid approvals</strong> filter.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {PAYMENT_HISTORY_STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                statusFilter === f.id
                  ? f.accent
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-violet-600 text-white border-violet-600'
                  : f.accent
                    ? 'bg-orange-50 text-orange-900 border-orange-200 hover:border-orange-300'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-violet-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppButton
            type="button"
            variant="primary"
            icon="search"
            disabled={loading}
            onClick={() => load(1)}
          >
            Apply Filters
          </AppButton>
          <AppButton
            type="button"
            variant="secondary"
            icon="x"
            disabled={loading}
            onClick={clearPaymentHistoryFilters}
          >
            Clear Filters
          </AppButton>
          <RefreshBtn onClick={() => load(page)} disabled={loading} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="search"
          placeholder="Search by member name, email, or transaction ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <ScrollableTable
          stickyFirstColumn
          mobileMaxHeight="min(50dvh, 420px)"
          desktopMaxHeight="min(68vh, 680px)"
          mobileScrollHint="Swipe sideways — Date stays visible while you scroll columns"
          ariaLabel="Payment history table"
        >
          <table className="w-full text-sm" style={{ minWidth: '1100px' }} data-list-table>
            <thead>
              <tr>
                {['Date', 'Member', 'Plan', 'Amount', 'Amount check', 'Status', 'Reviewed by', 'Actions'].map((h) => (
                  <th key={h} className="px-5 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <HistorySkeletonRow key={i} />)
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center">
                    <p className="text-slate-500 font-semibold">No payment records found</p>
                    <p className="text-xs text-slate-400 mt-1">Try a different filter or search term.</p>
                  </td>
                </tr>
              ) : (
                rows.map((u) => {
                  const badge = paymentProofStatusBadge(u);
                  return (
                    <tr
                      key={u.payment_proof_id ?? u.id}
                      className={`list-stripe-row transition-colors ${
                        u.amount_plan_mismatch ? 'row-amount-mismatch' : ''
                      }`}
                    >
                      <td className="px-5 py-4 text-xs text-slate-600 whitespace-nowrap">
                        {formatAdminDateTime(u.reviewed_at || u.uploaded_at)}
                      </td>
                      <td className="px-5 py-4">
                        <TruncateMemberCell username={u.username} email={u.email} />
                      </td>
                      <td className="px-5 py-4">
                        <AdminPlanTypeBadge isFullSettlement={u.plan_is_full_settlement} label={u.current_plan} />
                        {u.installment_label && !u.plan_is_full_settlement ? (
                          <p className="text-[10px] text-slate-400 mt-1">{u.installment_label}</p>
                        ) : null}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <p className="font-black text-slate-900">{fmtMoney(u.current_payment)}</p>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        {u.amount_plan_mismatch ? (
                          <div className="space-y-1">
                            <PaymentHistoryAmountMismatchBadge row={u} />
                            <p className="text-[10px] text-orange-900 font-semibold leading-snug">
                              Paid {fmtMoney(u.current_payment)} · Expected {fmtMoney(u.expected_amount)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-[10px] font-semibold text-emerald-700">OK</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs font-semibold text-slate-700 whitespace-nowrap">
                        {paymentReviewedByLabel(u)}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="inline-flex flex-nowrap items-center gap-1.5">
                          <PaymentActionButton
                            title="View payment screenshot"
                            onClick={() => viewPaymentProof(u)}
                            className="border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-violet-200 hover:text-violet-600"
                          >
                            <IconEye />
                          </PaymentActionButton>
                          <PaymentActionButton
                            title="Open full payment details"
                            onClick={() => openDetails(u)}
                            className="border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
                          >
                            <span className="text-[10px] font-black">Details</span>
                          </PaymentActionButton>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </ScrollableTable>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => load(p)}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={totalCount}
          rowsPerPageId="admin-payment-history-rows-per-page"
          embedded
          className="px-5"
        />
      </div>

      <MediaPreviewModal
        open={Boolean(mediaPreview)}
        src={mediaPreview?.src}
        title={mediaPreview?.title}
        subtitle={mediaPreview?.subtitle}
        onClose={() => setMediaPreview(null)}
      />

      {detailRow && (
        <Modal
          title="Member payment"
          subtitle={`${detailRow.username} · Payment #${detailRow.payment_proof_id ?? detailRow.id}`}
          onClose={closeDetails}
          largeClose
          maxWidthClass="max-w-2xl"
        >
          {auditLoading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading payment details…</p>
          ) : (
            <div className="space-y-4">
              {detailRow.amount_plan_mismatch ? (
                <div className="rounded-xl border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-950">
                  <p className="font-bold">Amount does not match this plan</p>
                  <p className="mt-1 text-xs leading-relaxed">
                    {detailRow.amount_mismatch_message || detailRow.payment_validation?.approval_blocked_reason || (
                      <>This payment is marked Paid but the amount does not match the selected plan. Use <strong>Revert back to Waiting list</strong> below, then collect the correct amount and approve again.</>
                    )}
                  </p>
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-slate-500">Status</p>
                  <p className="font-bold text-slate-900 mt-0.5">{detailRow.proof_status_label || detailRow.proof_status}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-slate-500">Amount paid</p>
                  <p className="font-bold text-slate-900 mt-0.5">{fmtMoney(detailRow.current_payment)}</p>
                  {detailRow.amount_plan_mismatch && detailRow.expected_amount ? (
                    <p className="text-[10px] text-orange-800 font-semibold mt-1">
                      Plan expected {fmtMoney(detailRow.expected_amount)}
                    </p>
                  ) : null}
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-slate-500">Transaction ID</p>
                  <p className="font-bold text-slate-900 mt-0.5 break-all">{detailRow.transaction_id || '—'}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-slate-500">Reviewed by</p>
                  <p className="font-bold text-slate-900 mt-0.5">{detailRow.reviewed_by_username || auditData?.reviewed_by_username || '—'}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
                  <p className="text-slate-500">Plan progress</p>
                  <p className="font-bold text-slate-900 mt-0.5">
                    {fmtMoney(detailRow.total_paid)} paid · {fmtMoney(detailRow.remaining)} remaining
                  </p>
                </div>
                {detailRow.admin_note ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 sm:col-span-2">
                    <p className="text-amber-900 font-bold">Admin note</p>
                    <p className="text-amber-950 mt-1">{detailRow.admin_note}</p>
                  </div>
                ) : null}
                {detailRow.approval_revert_reason || auditData?.approval_revert_reason ? (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 sm:col-span-2">
                    <p className="text-orange-950 font-bold">Why it was sent back</p>
                    <p className="text-orange-900 mt-1">{detailRow.approval_revert_reason || auditData?.approval_revert_reason}</p>
                  </div>
                ) : null}
              </div>

              {(auditData?.payment_validation || detailRow.payment_validation || detailRow.amount_plan_mismatch) && (
                <PaymentValidationSummary
                  row={{
                    payment_validation: auditData?.payment_validation || detailRow.payment_validation,
                    was_approval_reverted: auditData?.was_approval_reverted ?? detailRow.was_approval_reverted,
                    proof_status: detailRow.proof_status || auditData?.proof_status,
                    expected_amount: detailRow.expected_amount,
                    current_payment: detailRow.current_payment,
                  }}
                  fmtMoney={fmtMoney}
                />
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => viewPaymentProof(detailRow)}
                  className="px-4 py-2 text-xs font-bold text-violet-700 border border-violet-200 rounded-xl bg-violet-50 hover:bg-violet-100"
                >
                  View payment photo
                </button>
              </div>

              {(auditData?.can_revert_approval || detailRow.can_revert_approval) && (
                <AdminUndoApprovalPanel
                  phase={showRevertConfirm ? 'confirm' : 'intro'}
                  revertReason={revertReason}
                  onRevertReasonChange={setRevertReason}
                  onStartConfirm={() => setShowRevertConfirm(true)}
                  onCancelConfirm={() => { setShowRevertConfirm(false); setRevertReason(''); }}
                  onSubmit={handleRevertApproval}
                  submitting={revertSubmitting}
                />
              )}

              <div>
                <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">What happened (step by step)</p>
                <div className="max-h-[min(40vh,320px)] overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {(auditData?.attempt_timeline?.length
                    ? auditData.attempt_timeline
                    : auditData?.session_audit_logs?.length
                      ? auditData.session_audit_logs
                      : auditData?.audit_logs || []
                  ).map((entry, idx) => (
                    <div key={`${entry.action}-${entry.created_at}-${idx}`} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{entry.action_label || entry.action}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {entry.actor ? `By ${entry.actor}` : 'System / member'}
                          </p>
                          {entry.metadata?.admin_note ? (
                            <p className="text-[11px] text-amber-800 mt-1">Note: {entry.metadata.admin_note}</p>
                          ) : null}
                        </div>
                        <span className="text-[10px] text-slate-400 whitespace-nowrap">
                          {formatAdminDateTime(entry.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {!auditData?.attempt_timeline?.length &&
                    !auditData?.session_audit_logs?.length &&
                    !auditData?.audit_logs?.length && (
                      <p className="text-sm text-slate-500 py-8 text-center">No activity recorded yet.</p>
                    )}
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: WITHDRAWALS → ./admin/WithdrawalsTab.js
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
//  TAB: COMMUNITY POSTS
// ════════════════════════════════════════════════════════════════

function CommunityTab() {
  const [posts,   setPosts]   = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [modal,   setModal]   = useState(null);  // null | 'add' | post obj
  const [form,    setForm]    = useState({ title: '', body: '', post_type: 'announcement', is_pinned: false, event_date: '' });
  const [saving,  setSaving]  = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [toast,   setToast]   = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get(API.COMMUNITY, { params: { page, page_size: pageSize } });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setPosts(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch { setError('Failed to load posts.'); }
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const blank = { title: '', body: '', post_type: 'announcement', is_pinned: false, event_date: '' };
  const openAdd  = () => { setForm(blank); setSaveErr(''); setModal('add'); };
  const openEdit = (p) => { setForm({ title: p.title, body: p.body, post_type: p.post_type, is_pinned: p.is_pinned, event_date: p.event_date || '' }); setSaveErr(''); setModal(p); };

  // ✅ FIX: pass admin token in request + send correct payload
  const handleSave = async () => {
    if (!form.title.trim()) { setSaveErr('Title is required.'); return; }
    if (!form.body.trim())  { setSaveErr('Body content is required.'); return; }
    setSaving(true); setSaveErr('');

    const payload = {
      title:      form.title.trim(),
      body:       form.body.trim(),
      post_type:  form.post_type,
      is_pinned:  !!form.is_pinned,
      event_date: form.event_date || null,
    };

    try {
      if (modal === 'add') {
        await api.post(API.COMMUNITY_CREATE, payload, { headers: adminHeaders() });
        showToast('✅ Post published!');
      } else {
        await api.patch(API.COMMUNITY_POST(modal.id), payload, { headers: adminHeaders() });
        showToast('✅ Post updated!');
      }
      setModal(null);
      load();
    } catch (e) {
      const err = e.response?.data;
      setSaveErr(typeof err === 'object' ? Object.values(err).flat().join(' ') : 'Save failed. Please try again.');
    }
    setSaving(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(API.COMMUNITY_POST(id), { headers: adminHeaders() });
      showToast('🗑️ Post deleted.');
      load();
    } catch { showToast('❌ Delete failed.'); }
  };

  const TYPE_CONFIG = {
    announcement: { pill: 'bg-blue-50 text-blue-600 border-blue-200',      icon: '📣' },
    event:        { pill: 'bg-violet-50 text-violet-600 border-violet-200', icon: '📅' },
    training:     { pill: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: '🎓' },
    news:         { pill: 'bg-amber-50 text-amber-600 border-amber-200',    icon: '📰' },
  };

  return (
    <div className="space-y-4">
      <Toast msg={toast} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-slate-400 font-semibold">{posts.length} post{posts.length !== 1 ? 's' : ''}</span>
        <div className="flex gap-2">
          <RefreshBtn onClick={load} />
          <PrimaryBtn onClick={openAdd}>+ New Post</PrimaryBtn>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">⚠️ {error}</div>}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
              <div className="h-3 bg-slate-100 rounded w-24 mb-3" />
              <div className="h-4 bg-slate-100 rounded w-2/3 mb-2" />
              <div className="h-3 bg-slate-100 rounded w-full" />
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-20 text-center">
          <p className="text-5xl mb-3">📣</p>
          <p className="text-slate-500 font-semibold">No posts yet</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Create your first post to share with members</p>
          <PrimaryBtn onClick={openAdd}>+ Create First Post</PrimaryBtn>
        </div>
      ) : (
        <>
        <div className="space-y-3">
          {posts.map(post => {
            const t = TYPE_CONFIG[post.post_type] || TYPE_CONFIG.announcement;
            return (
              <div key={post.id} className={`bg-white rounded-2xl overflow-hidden border transition-shadow hover:shadow-sm ${post.is_pinned ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {post.is_pinned && <span className="text-[10px] font-black bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">📌 Pinned</span>}
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border capitalize ${t.pill}`}>{t.icon} {post.post_type}</span>
                        <span className="text-xs text-slate-400">{formatAppDate(post.created_at)}</span>
                      </div>
                      <h3 className="text-base font-black text-slate-900 mb-1">{post.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{post.body}</p>
                      {post.event_date && (
                        <div className="mt-3 inline-flex items-center gap-2 bg-violet-50 border border-violet-100 px-3 py-1.5 rounded-lg">
                          <span className="text-xs font-bold text-violet-700">📅 {formatAppDateTime(post.event_date)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <GhostBtn className="text-xs px-3 py-1.5" onClick={() => openEdit(post)}>✏️ Edit</GhostBtn>
                      <DangerBtn className="text-xs px-3 py-1.5" onClick={() => handleDelete(post.id, post.title)}>🗑️</DangerBtn>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={totalCount}
          rowsPerPageId="admin-community-rows-per-page"
        />
        </>
      )}

      {modal && (
        <Modal
          title={modal === 'add' ? '📣 Create New Post' : '✏️ Edit Post'}
          subtitle={modal === 'add' ? 'Publish to all members' : `Editing: ${modal.title}`}
          onClose={() => setModal(null)}
        >
          <div className="space-y-4">
            <FieldInput label="Title *" value={form.title} onChange={e => setF('title', e.target.value)} placeholder="e.g. New training module released" autoFocus />
            <FieldTextarea label="Body / Content *" value={form.body} onChange={e => setF('body', e.target.value)} placeholder="Write your message here…" />
            <FieldSelect label="Post Type" value={form.post_type} onChange={e => setF('post_type', e.target.value)}>
              <option value="announcement">📣 Announcement</option>
              <option value="event">📅 Event</option>
              <option value="training">🎓 Training</option>
              <option value="news">📰 News</option>
            </FieldSelect>
            {form.post_type === 'event' && (
              <FieldInput label="Event Date & Time" type="datetime-local" value={form.event_date} onChange={e => setF('event_date', e.target.value)} />
            )}
            <label className="flex items-center gap-3 cursor-pointer p-3.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
              <input type="checkbox" checked={!!form.is_pinned} onChange={e => setF('is_pinned', e.target.checked)} className="w-4 h-4 accent-violet-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">📌 Pin this post</p>
                <p className="text-xs text-slate-400 mt-0.5">Pinned posts appear at the top for all members</p>
              </div>
            </label>
            <ErrBox msg={saveErr} />
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <GhostBtn className="flex-1" onClick={() => setModal(null)}>Cancel</GhostBtn>
              <PrimaryBtn className="flex-1" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : modal === 'add' ? 'Publish Post →' : 'Save Changes →'}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TAB: PRODUCTS (catalog — CRUD flows TBD)
// ════════════════════════════════════════════════════════════════

const IconPencil = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const IconTrash = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconCloseSm = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usingDummy, setUsingDummy] = useState(false);
  const [imgErr, setImgErr] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [formName, setFormName] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState({ msg: '', variant: 'default' });
  const [formErr, setFormErr] = useState('');
  const [mediaPreview, setMediaPreview] = useState(null);
  const fileInputRef = useRef(null);
  const toastTimerRef = useRef(null);

  const viewProductImage = (src, name) => {
    const result = handleMediaView(src, {
      title: 'Product image',
      subtitle: name,
      onPreview: setMediaPreview,
    });
    if (!result.ok) showToast('No image available for this product.', 'error');
    else if (result.kind === 'document') showToast('Opening document in a new tab…', 'default');
  };

  const showToast = (msg, variant = 'default') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, variant });
    toastTimerRef.current = setTimeout(() => {
      setToast({ msg: '', variant: 'default' });
      toastTimerRef.current = null;
    }, 3800);
  };

  const resetProductForm = useCallback(() => {
    setFormMode('create');
    setEditingId(null);
    setExistingImageUrl('');
    setFormName('');
    setFormPrice('');
    setImageFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setFormErr('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const closeProductForm = () => {
    setFormOpen(false);
    resetProductForm();
  };

  const openCreateForm = () => {
    resetProductForm();
    setFormMode('create');
    setFormOpen(true);
  };

  const openEditForm = (p) => {
    resetProductForm();
    setFormMode('edit');
    setEditingId(p.id);
    setFormName(p.name ?? '');
    setFormPrice(p.price != null && p.price !== '' ? String(p.price) : '');
    setExistingImageUrl(resolveMediaSrc(p.image || ''));
    setFormOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setUsingDummy(false);
    try {
      const r = await api.get(API.ADMIN_PRODUCTS, {
        params: { page, page_size: pageSize },
        headers: adminHeaders(),
      });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setProducts(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch {
      setProducts(DUMMY_ADMIN_PRODUCTS);
      setUsingDummy(true);
      setTotalPages(1);
      setTotalCount(DUMMY_ADMIN_PRODUCTS.length);
    }
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    setFormErr('');
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      setFormErr('Please choose an image file (PNG, JPG, or WebP).');
      e.target.value = '';
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      setFormErr('Image must be 6 MB or smaller.');
      e.target.value = '';
      return;
    }
    setImageFile(f);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const clearImage = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFormErr('');
  };

  const normalizeCreatedProduct = (data) => {
    if (!data || typeof data !== 'object') return null;
    const id = data.id ?? `local-${Date.now()}`;
    const name = data.name ?? '';
    const price = Number(data.price ?? data.amount ?? 0);
    let image = data.image ?? data.image_url ?? '';
    if (image && typeof image === 'string') {
      if (image.startsWith('blob:') || image.startsWith('http://') || image.startsWith('https://')) {
        /* keep */
      } else if (image.startsWith('/')) {
        const origin = (process.env.REACT_APP_API_ENDPOINT || 'http://127.0.0.1:8000').replace(/\/$/, '');
        image = `${origin}${image}`;
      } else {
        const origin = (process.env.REACT_APP_API_ENDPOINT || 'http://127.0.0.1:8000').replace(/\/$/, '');
        image = `${origin}/${image.replace(/^\//, '')}`;
      }
    }
    return { id, name, price, image, sku: data.sku };
  };

  const handleProductFormSubmit = async () => {
    const name = formName.trim();
    const priceNum = parseFloat(String(formPrice).replace(/,/g, ''));
    if (!name) {
      setFormErr('Enter a product name.');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setFormErr('Enter a valid price.');
      return;
    }

    if (formMode === 'create') {
      if (!imageFile) {
        setFormErr('Please upload a product image.');
        return;
      }

      setFormErr('');
      setSaving(true);

      const fd = new FormData();
      fd.append('name', name);
      fd.append('price', String(priceNum));
      fd.append('image', imageFile);

      let serverProduct = null;
      try {
        const res = await api.post(API.ADMIN_PRODUCTS, fd, { headers: adminHeaders() });
        serverProduct = normalizeCreatedProduct(res.data);
      } catch {
        serverProduct = null;
      }

      if (serverProduct && serverProduct.name) {
        setProducts((prev) => [serverProduct, ...prev]);
        showToast('✅ Product created');
      } else {
        const catalogImageUrl = URL.createObjectURL(imageFile);
        setProducts((prev) => [
          { id: `local-${Date.now()}`, name, price: priceNum, image: catalogImageUrl },
          ...prev,
        ]);
        showToast('✅ Product created');
      }

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSaving(false);
      closeProductForm();
      return;
    }

    // edit
    if (!imageFile && !existingImageUrl) {
      setFormErr('Add a product image.');
      return;
    }

    setFormErr('');
    setSaving(true);

    const fd = new FormData();
    fd.append('name', name);
    fd.append('price', String(priceNum));
    if (imageFile) fd.append('image', imageFile);

    let serverProduct = null;
    try {
      const res = await api.patch(API.ADMIN_PRODUCT(editingId), fd, { headers: adminHeaders() });
      serverProduct = normalizeCreatedProduct(res.data);
    } catch {
      serverProduct = null;
    }

    if (serverProduct && serverProduct.name) {
      setProducts((prev) =>
        prev.map((x) => (String(x.id) === String(editingId) ? { ...x, ...serverProduct } : x))
      );
      showToast('✅ Product updated');
    } else {
      setProducts((prev) =>
        prev.map((x) => {
          if (String(x.id) !== String(editingId)) return x;
          let nextImage = x.image;
          if (imageFile) {
            if (typeof x.image === 'string' && x.image.startsWith('blob:')) URL.revokeObjectURL(x.image);
            nextImage = URL.createObjectURL(imageFile);
          }
          return { ...x, name, price: priceNum, image: nextImage };
        })
      );
      showToast('✅ Product updated');
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSaving(false);
    closeProductForm();
  };

  const handleDeleteProduct = async (product) => {
    const productName = product.name?.trim() || 'this product';
    const confirmed = window.confirm(
      `Are you sure you want to delete "${productName}"?`
    );
    if (!confirmed) return;

    const productId = product.id;
    if (String(productId).startsWith('local-')) {
      setProducts((prev) => prev.filter((x) => String(x.id) !== String(productId)));
      showToast('Successfully deleted the product.', 'success');
      return;
    }

    setDeletingId(productId);
    try {
      await api.delete(API.ADMIN_PRODUCT(productId), { headers: adminHeaders() });
      setProducts((prev) => prev.filter((x) => String(x.id) !== String(productId)));
      showToast('Successfully deleted the product.', 'success');
    } catch {
      showToast('Failed to delete the product.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <Toast msg={toast.msg} variant={toast.variant} />

      {usingDummy && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50/90 text-amber-950">
          <span className="text-lg flex-shrink-0" aria-hidden>📦</span>
          <div className="min-w-0 text-sm">
            <p className="font-bold text-amber-900">Sample catalog</p>
            <p className="text-amber-800/90 text-xs mt-1 leading-relaxed">
              Showing demo products until <code className="font-mono text-[11px] bg-amber-100/80 px-1 rounded">GET /api/admin/products/</code> returns data.
              New products you create are added to this list; if <code className="font-mono text-[11px] bg-amber-100/80 px-1 rounded">POST</code> is not live yet, images stay in this session only.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <RefreshBtn onClick={load} />
          <span className="text-xs text-slate-400 font-semibold">
            {loading ? '…' : `${products.length} product${products.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        <PrimaryBtn type="button" className="shrink-0 shadow-md shadow-violet-500/20 ml-auto" onClick={openCreateForm}>
          ＋ Create product
        </PrimaryBtn>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-slate-100" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-slate-100 rounded-lg w-4/5" />
                <div className="h-5 bg-slate-100 rounded-lg w-2/5" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-dashed border-slate-200 bg-white">
          <p className="text-4xl mb-3">🛍️</p>
          <p className="text-slate-600 font-semibold">No products yet</p>
          <p className="text-slate-400 text-sm mt-1 max-w-xs mx-auto">Use Create product to add your first item.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map((p) => {
            const src = resolveMediaSrc(p.image || '');
            const broken = imgErr[p.id];
            return (
              <article
                key={p.id}
                className="group relative bg-white rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-xl hover:border-violet-200/80 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="absolute top-3 right-3 z-20 flex items-center gap-1">
                  <button
                    type="button"
                    title="Edit product"
                    className="w-9 h-9 rounded-xl bg-white/95 border border-slate-200/80 text-slate-500 hover:text-violet-600 hover:border-violet-200 shadow-sm flex items-center justify-center transition-colors backdrop-blur-sm"
                    onClick={() => openEditForm(p)}
                  >
                    <IconPencil />
                  </button>
                  <button
                    type="button"
                    title="Delete product"
                    disabled={deletingId === p.id}
                    className="w-9 h-9 rounded-xl bg-white/95 border border-slate-200/80 text-slate-500 hover:text-red-500 hover:border-red-200 shadow-sm flex items-center justify-center transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleDeleteProduct(p)}
                  >
                    <IconTrash />
                  </button>
                </div>

                <button
                  type="button"
                  title="View product image"
                  disabled={!src || broken}
                  onClick={() => viewProductImage(src, p.name)}
                  className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50 text-left disabled:cursor-default"
                >
                  {src && !broken ? (
                    <img
                      src={src}
                      alt={p.name || 'Product'}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={() => setImgErr((m) => ({ ...m, [p.id]: true }))}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                      <span className="text-4xl">🖼️</span>
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-60" />
                  {src && !broken ? (
                    <div className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/30 bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                      <IconEye className="h-4 w-4" />
                    </div>
                  ) : null}
                </button>

                <div className="p-4 pt-3 flex-1 flex flex-col border-t border-slate-50">
                  <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 min-h-[2.5rem]" title={p.name}>
                    {p.name}
                  </h3>
                  <p className="mt-2 text-lg font-black text-violet-600 tracking-tight">
                    ₹{Number(p.price ?? 0).toLocaleString('en-IN')}
                  </p>
                  {p.sku != null && p.sku !== '' && (
                    <p className="mt-auto pt-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">SKU {p.sku}</p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={totalCount}
          rowsPerPageId="admin-products-rows-per-page"
        />
        </>
      )}

      {formOpen && (
        <Modal
          title={formMode === 'create' ? 'New product' : 'Edit product'}
          subtitle={
            formMode === 'create'
              ? 'Add a name, price, and image for the catalog'
              : 'Update name, price, or replace the image'
          }
          onClose={() => !saving && closeProductForm()}
          largeClose
          maxWidthClass="max-w-md"
        >
          <div className="space-y-5">
            <input
              id="admin-product-image-input"
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="sr-only"
              onChange={pickImage}
            />

            <FieldInput
              label="Product name *"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Wireless earbuds"
              autoFocus={formMode === 'create'}
            />
            <FieldInput
              label="Price (₹) *"
              type="number"
              min="0"
              step="0.01"
              value={formPrice}
              onChange={(e) => setFormPrice(e.target.value)}
              placeholder="e.g. 1999"
            />

            <div>
              <Label>{formMode === 'create' ? 'Product image *' : 'Product image'}</Label>
              <p className="text-[10px] text-slate-400 mt-0.5 mb-1.5">
                {formMode === 'edit' ? 'Leave as-is or choose a new file to replace the current image.' : 'PNG, JPG or WebP · max 6 MB'}
              </p>
              <div className="mt-1.5 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/80 hover:border-violet-300 hover:bg-violet-50/30 transition-colors overflow-hidden">
                {imageFile ? (
                  <div className="flex items-center gap-3 p-3 bg-white">
                    <button
                      type="button"
                      title="View selected image"
                      disabled={!previewUrl}
                      onClick={() => viewProductImage(previewUrl, formName || imageFile.name)}
                      className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-colors hover:border-violet-200 hover:ring-2 hover:ring-violet-100 disabled:cursor-default disabled:hover:border-slate-200 disabled:hover:ring-0"
                    >
                      {previewUrl ? (
                        <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                      ) : null}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Selected file</p>
                      <p className="text-sm font-bold text-slate-900 truncate" title={imageFile.name}>
                        {imageFile.name}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{(imageFile.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button
                      type="button"
                      title="Remove image"
                      aria-label="Remove image"
                      onClick={clearImage}
                      className="w-9 h-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 flex items-center justify-center transition-colors shrink-0"
                    >
                      <IconCloseSm className="w-4 h-4" />
                    </button>
                  </div>
                ) : formMode === 'edit' && existingImageUrl ? (
                  <div className="flex items-center gap-3 p-3 bg-white">
                    <button
                      type="button"
                      title="View current image"
                      onClick={() => viewProductImage(existingImageUrl, formName || 'Product')}
                      className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-colors hover:border-violet-200 hover:ring-2 hover:ring-violet-100"
                    >
                      <img src={existingImageUrl} alt="" className="h-full w-full object-cover" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Current image</p>
                      <p className="text-sm font-bold text-slate-900 truncate" title={existingImageUrl}>
                        {existingImageUrl.startsWith('blob:')
                          ? 'Session preview'
                          : decodeURIComponent((existingImageUrl.split('/').pop() || '').split('?')[0] || 'Image')}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-xs font-semibold px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="admin-product-image-input"
                    className="flex flex-col items-center justify-center cursor-pointer px-4 py-8 gap-2"
                  >
                    <span className="text-2xl" aria-hidden>📤</span>
                    <span className="text-xs font-bold text-slate-600">Click to upload</span>
                    <span className="text-[10px] text-slate-400">PNG, JPG or WebP · max 6 MB</span>
                  </label>
                )}
              </div>
            </div>

            <ErrBox msg={formErr} />

            <div className="flex gap-3 pt-1 border-t border-slate-100">
              <GhostBtn type="button" className="flex-1" disabled={saving} onClick={closeProductForm}>
                Cancel
              </GhostBtn>
              <PrimaryBtn type="button" className="flex-1" disabled={saving} onClick={handleProductFormSubmit}>
                {saving ? (formMode === 'create' ? 'Creating…' : 'Saving…') : formMode === 'create' ? 'Create product' : 'Update product'}
              </PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}

      <MediaPreviewModal
        open={Boolean(mediaPreview)}
        src={mediaPreview?.src}
        title={mediaPreview?.title}
        subtitle={mediaPreview?.subtitle}
        onClose={() => setMediaPreview(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  ACCOUNT RECOVERY TAB
// ════════════════════════════════════════════════════════════════

function AccountRecoveryTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [toast, setToast] = useState({ msg: '', variant: 'default' });
  const [generateOpen, setGenerateOpen] = useState(false);
  const [genEmail, setGenEmail] = useState('');
  const [genAmount, setGenAmount] = useState('');
  const [genPreview, setGenPreview] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [genSubmitting, setGenSubmitting] = useState(false);
  const [lastLink, setLastLink] = useState('');
  const [viewMemberId, setViewMemberId] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionId, setActionId] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);

  const showToast = (msg, variant = 'default') => {
    setToast({ msg, variant });
    setTimeout(() => setToast({ msg: '', variant: 'default' }), 3800);
  };

  const load = useCallback(async (pageNum = 1, overrides = null) => {
    setLoading(true);
    setError('');
    const status = overrides?.statusFilter !== undefined
      ? overrides.statusFilter
      : statusFilter;
    try {
      const params = { page: pageNum, page_size: pageSize };
      if (status) params.status = status;
      const r = await api.get(API.ADMIN_RECOVERY_LIST, { params, headers: adminHeaders() });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setRows(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (e) {
      setRows([]);
      setTotalPages(1);
      setTotalCount(0);
      setError(getApiErrorMessage(e.response?.data, e.message || 'Failed to load recovery queue.'));
    }
    setLoading(false);
  }, [pageSize, statusFilter]);

  const clearRecoveryFilters = () => {
    setStatusFilter('submitted');
    load(1, { statusFilter: 'submitted' });
  };

  // Initial + page-size only. Status filter applies via Apply Filters.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional Apply Filters pattern
  }, [pageSize]);

  useEffect(() => {
    const email = (searchParams.get('recovery_email') || '').trim();
    if (searchParams.get('recovery_generate') !== '1' || !email) return;

    const next = new URLSearchParams(searchParams);
    next.delete('recovery_email');
    next.delete('recovery_generate');
    setSearchParams(next, { replace: true });

    setGenEmail(email);
    setGenerateOpen(true);
    setGenLoading(true);
    setGenPreview(null);
    api.get(API.ADMIN_RECOVERY_PREVIEW, { params: { email }, headers: adminHeaders() })
      .then((r) => {
        setGenPreview(r.data);
        setGenAmount(r.data.amount_total || '');
      })
      .catch((e) => {
        showToast(e.response?.data?.error || 'Could not preview dues.', 'error');
      })
      .finally(() => setGenLoading(false));
  }, [searchParams, setSearchParams]);

  const previewGenerate = async () => {
    const email = genEmail.trim();
    if (!email) return;
    setGenLoading(true);
    setGenPreview(null);
    try {
      const r = await api.get(API.ADMIN_RECOVERY_PREVIEW, {
        params: { email },
        headers: adminHeaders(),
      });
      setGenPreview(r.data);
      setGenAmount(r.data.amount_total || '');
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not preview dues.', 'error');
    }
    setGenLoading(false);
  };

  const submitGenerate = async () => {
    const email = genEmail.trim();
    if (!email) return;
    setGenSubmitting(true);
    try {
      const body = { email };
      if (genAmount.trim()) body.amount = genAmount.trim();
      const r = await api.post(API.ADMIN_RECOVERY_GENERATE, body, { headers: adminHeaders() });
      setLastLink(r.data.recovery_url || '');
      showToast('Recovery link generated.', 'success');
      setGenerateOpen(false);
      setGenEmail('');
      setGenPreview(null);
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to generate link.', 'error');
    }
    setGenSubmitting(false);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url || '');
    showToast('Link copied to clipboard.', 'success');
  };

  const handleApprove = async (row) => {
    setActionId(row.id);
    try {
      const r = await api.post(API.ADMIN_RECOVERY_APPROVE(row.id), {}, { headers: adminHeaders() });
      showToast(r.data?.message || 'Approved and unblocked.', 'success');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Approve failed.', 'error');
    }
    setActionId(null);
  };

  const handleReject = async () => {
    if (!rejectRow || rejectNote.trim().length < 5) {
      showToast('Rejection reason must be at least 5 characters.', 'error');
      return;
    }
    setActionId(rejectRow.id);
    try {
      await api.post(
        API.ADMIN_RECOVERY_REJECT(rejectRow.id),
        { admin_note: rejectNote.trim() },
        { headers: adminHeaders() },
      );
      showToast('Recovery payment rejected.', 'success');
      setRejectRow(null);
      setRejectNote('');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Reject failed.', 'error');
    }
    setActionId(null);
  };

  const fmtMoney = (n) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;

  const statusBadge = (s) => {
    const map = {
      submitted: 'bg-amber-50 text-amber-800 border-amber-200',
      link_generated: 'bg-slate-50 text-slate-700 border-slate-200',
      approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
    };
    return map[s] || map.link_generated;
  };

  if (viewMemberId) {
    return (
      <MemberProfileView
        memberId={viewMemberId}
        onBack={() => setViewMemberId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Toast msg={toast.msg} variant={toast.variant} />

      {lastLink && (
        <div className="p-4 rounded-2xl border border-violet-200 bg-violet-50 flex flex-wrap items-center gap-3">
          <span className="text-sm text-violet-900 flex-1 min-w-0 break-all">{lastLink}</span>
          <GhostBtn type="button" onClick={() => copyLink(lastLink)}>Copy link</GhostBtn>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          {['submitted', 'link_generated', 'approved', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                statusFilter === s
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {s ? s.replace('_', ' ') : 'All'}
            </button>
          ))}
          <AppButton
            type="button"
            variant="primary"
            icon="search"
            disabled={loading}
            onClick={() => load(1)}
          >
            Apply Filters
          </AppButton>
          <AppButton
            type="button"
            variant="secondary"
            icon="x"
            disabled={loading}
            onClick={clearRecoveryFilters}
          >
            Clear Filters
          </AppButton>
        </div>
        <PrimaryBtn type="button" onClick={() => setGenerateOpen(true)}>
          Generate payment link
        </PrimaryBtn>
      </div>

      <ErrBox msg={error} />

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <ScrollableTable
          stickyFirstColumn
          mobileScrollHint="Swipe sideways to view recovery status and actions"
          ariaLabel="Account recovery table"
        >
          <table className="w-full text-sm" style={{ minWidth: '960px' }} data-list-table>
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3 text-left">Member</th>
                <th className="px-4 py-3 text-left">Due</th>
                <th className="px-4 py-3 text-left">Months</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Link expires</th>
                <th className="px-4 py-3 text-left">Screenshot</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No recovery records.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="list-stripe-row transition-colors">
                  <td className="px-4 py-3">
                    <TruncateMemberCell
                      username={row.username}
                      email={row.email}
                      usernameClassName="font-semibold text-slate-900 max-w-[180px]"
                      emailClassName="text-xs text-slate-500 max-w-[200px]"
                    />
                  </td>
                  <td className="px-4 py-3 font-semibold">{fmtMoney(row.amount_total)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {(row.months || []).map((m) => m.installment_no).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusBadge(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {row.token_expires_at ? (
                      <>
                        <p className={row.is_link_expired ? 'text-red-600 font-semibold' : ''}>
                          {formatAppDateTime(row.token_expires_at)}
                        </p>
                        {row.is_link_expired ? (
                          <p className="text-[10px] text-red-500 mt-0.5">Expired</p>
                        ) : row.status === 'link_generated' ? (
                          <p className="text-[10px] text-slate-400 mt-0.5">Valid {row.link_valid_hours || 24}h from creation</p>
                        ) : null}
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {row.screenshot_url ? (
                      <button
                        type="button"
                        className="group relative h-12 w-12 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shadow-sm transition hover:border-violet-300 hover:ring-2 hover:ring-violet-100"
                        title="View payment screenshot"
                        onClick={() => setMediaPreview({
                          src: row.screenshot_url,
                          title: 'Payment screenshot',
                          subtitle: `${row.username} · ${row.email}`,
                        })}
                      >
                        <img
                          src={resolveMediaSrc(row.screenshot_url)}
                          alt={`Payment proof for ${row.username}`}
                          className="h-full w-full object-cover transition group-hover:scale-105"
                        />
                      </button>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <PaymentActionButton
                        title="View member"
                        onClick={() => setViewMemberId(row.user_id)}
                        className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        <IconEye />
                      </PaymentActionButton>
                      {row.recovery_url && row.status === 'link_generated' && !row.is_link_expired && (
                        <PaymentActionButton
                          title="Copy link"
                          onClick={() => copyLink(row.recovery_url)}
                          className="border-violet-200 text-violet-600 hover:bg-violet-50"
                        >
                          🔗
                        </PaymentActionButton>
                      )}
                      {row.status === 'submitted' && (
                        <>
                          <PaymentActionButton
                            title="Approve & unblock"
                            onClick={() => handleApprove(row)}
                            disabled={actionId === row.id}
                            className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                          >
                            <IconCheck />
                          </PaymentActionButton>
                          <PaymentActionButton
                            title="Reject"
                            onClick={() => { setRejectRow(row); setRejectNote(''); }}
                            className="border-red-200 text-red-500 hover:bg-red-50"
                          >
                            <IconX />
                          </PaymentActionButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTable>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => load(p)}
          pageSize={pageSize}
          onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
          disabled={loading}
          count={totalCount || rows.length}
          rowsPerPageId="admin-recovery-rows-per-page"
          embedded
          className="px-4"
        />
      </div>

      {generateOpen && (
        <Modal title="Generate recovery link" subtitle="Blocked member catch-up payment · link valid 24 hours" onClose={() => !genSubmitting && setGenerateOpen(false)}>
          <div className="space-y-4">
            <p className="text-xs text-slate-500 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              The member must pay and upload proof within <strong>24 hours</strong>. After that the link stops working and you must generate a new one.
            </p>
            <FieldInput
              label="Member email"
              value={genEmail}
              onChange={(e) => setGenEmail(e.target.value)}
              placeholder="member@example.com"
            />
            <GhostBtn type="button" onClick={previewGenerate} disabled={genLoading || !genEmail.trim()}>
              {genLoading ? 'Loading…' : 'Preview dues'}
            </GhostBtn>
            {genPreview && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
                <p><strong>{genPreview.user?.username}</strong> — {genPreview.month_count} month(s)</p>
                <ul className="text-xs">
                  {(genPreview.months || []).map((m) => (
                    <li key={m.installment_no}>Month {m.installment_no}: ₹{m.amount}</li>
                  ))}
                </ul>
              </div>
            )}
            <FieldInput
              label="Total amount (must match preview)"
              value={genAmount}
              onChange={(e) => setGenAmount(e.target.value)}
              placeholder="Auto-calculated from missed months"
              readOnly={Boolean(genPreview?.amount_total)}
            />
            <PrimaryBtn type="button" className="w-full" onClick={submitGenerate} disabled={genSubmitting || !genEmail.trim()}>
              {genSubmitting ? 'Generating…' : 'Generate link'}
            </PrimaryBtn>
          </div>
        </Modal>
      )}

      {rejectRow && (
        <Modal title="Reject recovery payment" subtitle={rejectRow.email} onClose={() => !actionId && setRejectRow(null)}>
          <p className="text-xs text-amber-800 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 mb-3">
            The member stays blocked. This link is invalidated — generate a new recovery link if they should try again. The screenshot is kept for audit only.
          </p>
          <FieldTextarea label="Reason (shown internally)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Min 5 characters" />
          <div className="flex gap-3 mt-4">
            <GhostBtn className="flex-1" onClick={() => setRejectRow(null)}>Cancel</GhostBtn>
            <DangerBtn className="flex-1" onClick={handleReject} disabled={actionId === rejectRow.id}>Reject</DangerBtn>
          </div>
        </Modal>
      )}

      <MediaPreviewModal
        open={Boolean(mediaPreview)}
        src={mediaPreview?.src}
        title={mediaPreview?.title}
        subtitle={mediaPreview?.subtitle}
        onClose={() => setMediaPreview(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  TABS CONFIG + ROOT SHELL
// ════════════════════════════════════════════════════════════════

const TABS = [
  { id: 'overview',    icon: '📊', label: 'Overview',    desc: 'Platform stats, revenue and recent member activity.' },
  { id: 'members',     icon: '👥', label: 'Members',     desc: 'View member profiles, reset passwords, and add bonus points.' },
  { id: 'withdrawals', icon: '🏦', label: 'Withdrawals', desc: 'People waiting to be paid — approve, send money, upload proof.' },
  { id: 'recovery',    icon: '🔓', label: 'Account Recovery', desc: 'Generate payment links for blocked members, review proofs, and approve & unblock.' },
  { id: 'community',   icon: '📣', label: 'Community',   desc: 'Create and manage announcements, events, and training posts.' },
  { id: 'payments',    icon: '💳', label: 'Payments',    desc: 'Check payment photos — mark as Paid or Reject.' },
  { id: 'payment-history', icon: '📋', label: 'Payment History', desc: 'Find old payments, see photos, fix wrong Paid clicks.' },
  { id: 'referral-history', icon: '🔗', label: 'Referral History', desc: 'Monitor referral mapping, claims, and full audit timeline.' },
  { id: 'mlm-commission-log', icon: '💰', label: 'MLM Commission History', desc: 'Who earned from whom, EMI month, level, amount — credits and reversals.' },
  { id: 'products',    icon: '🛍️', label: 'Products',    desc: 'Manage the product catalog — images, names, and pricing.' },
];

function AdminPanel() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [adminUser,   setAdminUser]   = useState(() => {
    try { const s = localStorage.getItem('admin_user'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [activeTab,   setActiveTab]   = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab && TABS.some(t => t.id === tab) ? tab : 'overview';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats,       setStats]       = useState({ total_users: 0, active_members: 0, total_revenue: 0, pending_withdrawals: 0, pending_withdrawal_amount: 0, recent_members: [] });

  const handleAdminLogin  = (user) => setAdminUser(user);
  const handleAdminLogout = () => {
    ['admin_access_token', 'admin_refresh_token', 'admin_user'].forEach(k => localStorage.removeItem(k));
    const memberAccess = localStorage.getItem('access_token');
    if (memberAccess) {
      api.defaults.headers.common.Authorization = `Bearer ${memberAccess}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
    setAdminUser(null);
  };

  // ✅ FIX: always use admin_access_token
  const loadStats = useCallback(async () => {
    try {
      const r = await api.get(API.ADMIN_STATS, { headers: adminHeaders() });
      setStats(r.data);
    } catch (e) { console.error('Stats load failed:', e.response?.data || e.message); }
  }, []);

  useEffect(() => {
    if (!adminUser) return;
    loadStats();
  }, [adminUser, loadStats]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TABS.some(t => t.id === tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    if (tabId !== 'members') {
      next.delete('edit');
      next.delete('member');
    }
    if (tabId !== 'withdrawals') {
      next.delete('wd_user');
    }
    setSearchParams(next);
  };

  if (!adminUser) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  const currentTab = TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-slate-900 border-b border-slate-800 h-14 flex items-center px-4 sm:px-6 gap-4">
        <button className="lg:hidden w-8 h-8 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-white" onClick={() => setSidebarOpen(o => !o)}>
          <span className="block w-5 h-0.5 bg-current rounded-full" />
          <span className="block w-5 h-0.5 bg-current rounded-full" />
          <span className="block w-5 h-0.5 bg-current rounded-full" />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
            <span className="text-white text-xs font-black">S</span>
          </div>
          <span className="text-white font-black text-sm hidden sm:block" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>SuccessFlow</span>
          <span className="text-slate-700 hidden sm:block text-xs">·</span>
          <span className="text-slate-300 text-xs hidden sm:block font-semibold">{currentTab?.label}</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => navigate('/')} className="hidden sm:block text-slate-400 hover:text-white text-xs font-semibold transition-colors">← Site</button>
          <div className="flex items-center gap-2 bg-slate-800 rounded-full pl-2 pr-3 py-1.5">
            <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center text-white text-[10px] font-black">{adminUser.username?.[0]?.toUpperCase()}</div>
            <span className="text-slate-300 text-xs font-semibold hidden sm:block">{adminUser.username}</span>
          </div>
          <button onClick={handleAdminLogout} className="text-slate-500 hover:text-red-400 text-xs font-semibold transition-colors">Logout</button>
        </div>
      </header>

      <div className="flex">
        {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* Sidebar */}
        <aside className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] w-60 bg-white border-r border-slate-200 z-30 transform transition-transform duration-300 lg:translate-x-0 lg:sticky flex flex-col ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all text-left ${activeTab === tab.id ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
                <span className="text-base flex-shrink-0">{tab.icon}</span>
                <span className="flex-1">{tab.label}</span>
                {tab.id === 'withdrawals' && stats.pending_withdrawals > 0 && (
                  <span className="bg-amber-400 text-amber-900 text-[10px] font-black px-1.5 py-0.5 rounded-full">{stats.pending_withdrawals}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-slate-50">
              <div className="w-8 h-8 rounded-xl bg-violet-600 flex items-center justify-center text-white text-xs font-black flex-shrink-0">{adminUser.username?.[0]?.toUpperCase()}</div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{adminUser.username}</p>
                <p className="text-[10px] text-slate-400">Administrator</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-black text-slate-900">{currentTab?.icon} {currentTab?.label}</h1>
            <p className="text-sm text-slate-400 mt-1">{currentTab?.desc}</p>
          </div>
          {activeTab === 'overview'    && <OverviewTab stats={stats} onRefresh={loadStats} />}
          {activeTab === 'members'     && <MembersTab adminUser={adminUser} />}
          {activeTab === 'withdrawals' && <WithdrawalsTab />}
          {activeTab === 'recovery'    && <AccountRecoveryTab />}
          {activeTab === 'community'   && <CommunityTab />}
          {activeTab === 'payments'    && <PaymentsTab />}
          {activeTab === 'payment-history' && <PaymentHistoryTab />}
          {activeTab === 'referral-history' && <ReferralHistoryTab />}
          {activeTab === 'mlm-commission-log' && <MlmCommissionLogTab />}
          {activeTab === 'products'    && <ProductsTab />}
        </main>
      </div>
    </div>
  );
}

export default AdminPanel;